import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import type {
  AdminApi,
  ChecklistTemplateAdmin,
  ConfigurationImpactAssessment,
  InspectionCaseDetail,
  InspectionCaseSummary,
  InspectorQualification,
  PublishedTemplateVersionResult,
  TemplateImportProfile,
  TemplateImportRun,
  TemplateSourceArtifact,
  UserSummary,
} from './admin-api';
import { ApiError } from './api';
import { problemMessage, problemTechnicalDetail } from './admin-presentation';
import { PageHeader } from './admin-ui';
import {
  ASSESSMENTS,
  BINDING_KINDS,
  COLUMN_NAMES,
  FORMULA_CELL_POLICIES,
  ITEM_CODE_MODES_V2,
  OWNER_CODES,
  PLACEMENTS,
  VALUE_TYPES,
  type AnyDeclarativeMapping,
  type ColumnBinding,
  type ColumnName,
  type CorrectionDraft,
  type DeclarativeMapping,
  type DeclarativeMappingV2,
  type FailEvidenceRule,
  type FieldMapping,
  type FormulaCellPolicy,
  type HeaderAliasesV2,
  type ItemCodeModeV2,
  buildCorrectionPayload,
  buildAutoF3MappingV2Payload,
  buildMappingPayload,
  defaultMappingForFamily,
  emptyCorrectionDraft,
  emptyField,
  formatTokenList,
  isDeclarativeMappingV2,
  mappingFromUnknown,
  MAPPING_SCHEMA_V2,
  parseTokenList,
  replaceColumn,
  replaceTokens,
  validateGovernedSourceFamilyCorrection,
  validateGovernedSourceFamilyMapping,
  validateMappingAny,
} from './template-admin-mapping';
import {
  activeVersionLabel,
  assessmentLabel,
  assignmentUserCandidates,
  bindingKindLabel,
  canOpenImportRunInBuilder,
  caseBusinessLabel,
  columnNameLabel,
  currentChecklistAssignment,
  existingQualification,
  formulaPolicyLabel,
  importRunStatusLabel,
  inspectorCandidates,
  issueContext,
  normalizeLogicalFilename,
  profileStatusLabel,
  qualificationStatusLabel,
  templateBusinessLabel,
  TEMPLATE_APPLY_IMPACT_INTRO,
  TEMPLATE_APPLY_SUCCESS_NOTICE,
  templateActivationImpactRows,
  templatePrimaryDisplayName,
  userBusinessLabel,
  validationIssueExplanation,
} from './template-admin-presentation';
import { generateInternalCode } from './admin-internal-codes';
import { TemplateAuthoringAdmin } from './template-authoring-admin';
import { TemplateVersionRuntimePreview } from './template-version-runtime-preview';

const TABS = [
  { id: 'templates', label: 'Чек-листы' },
  { id: 'builder', label: 'Конструктор' },
  { id: 'import', label: 'Импорт Excel' },
  { id: 'qualifications', label: 'Допуски инспекторов' },
  { id: 'assignments', label: 'Назначения на осмотр' },
] as const;

type TemplatesFocus = {
  readonly templateId: string;
  readonly versionId: string;
};

type RuntimePreviewTarget = {
  readonly templateId: string;
  readonly templateDisplayName: string;
  readonly versionId: string;
  readonly versionCode: string;
  readonly isActive: boolean;
  readonly returnTo: 'list' | 'detail';
};

const AUTO_F3_CODE = 'AUTO-F3';
const AUTO_F3_FAMILY = 'F3';

type TemplateTab = (typeof TABS)[number]['id'];
type TemplateVersionRow = Awaited<ReturnType<AdminApi['listChecklistTemplateVersions']>>[number];
type ImportRevisionRow = Awaited<ReturnType<AdminApi['listImportRevisions']>>['items'][number];

function ProblemAlert(props: { readonly failure: unknown }): ReactElement {
  const technical = problemTechnicalDetail(props.failure);
  return (
    <div role="alert" className="banner banner-closed">
      <p>{problemMessage(props.failure)}</p>
      {technical !== null ? <p className="meta">Технические сведения: {technical}</p> : null}
    </div>
  );
}

function TechnicalDetails(props: { readonly children: ReactNode }): ReactElement {
  return (
    <details className="technical">
      <summary>Технические сведения</summary>
      {props.children}
    </details>
  );
}

async function resolveAutoF3Revision(
  api: AdminApi,
): Promise<{ profile: TemplateImportProfile; revisionId: string }> {
  const loadProfiles = () => api.listImportProfiles();
  let profiles = await loadProfiles();
  let profile = profiles.find((row) => row.code === AUTO_F3_CODE) ?? null;

  if (profile !== null && profile.source_family !== AUTO_F3_FAMILY) {
    throw new Error(
      'Профиль AUTO-F3 уже существует с другим семейством источника. Используйте «Расширенные настройки импорта».',
    );
  }

  if (profile === null) {
    try {
      profile = await api.createImportProfile(AUTO_F3_CODE, AUTO_F3_FAMILY);
    } catch (failure: unknown) {
      profiles = await loadProfiles();
      profile = profiles.find((row) => row.code === AUTO_F3_CODE) ?? null;
      if (profile === null) {
        throw failure instanceof Error
          ? failure
          : new Error('Не удалось создать системный профиль AUTO-F3.', { cause: failure });
      }
      if (profile.source_family !== AUTO_F3_FAMILY) {
        throw new Error(
          'Профиль AUTO-F3 уже существует с другим семейством источника. Используйте «Расширенные настройки импорта».',
          { cause: failure },
        );
      }
    }
  }

  const isMappingSchemaV2 = (mapping: unknown): boolean => {
    if (mapping === null || typeof mapping !== 'object' || Array.isArray(mapping)) {
      return false;
    }
    return (mapping as { schema?: unknown }).schema === MAPPING_SCHEMA_V2;
  };

  const loadCurrentRevision = async (profileId: string) => {
    const revisions = await api.listImportRevisions(profileId);
    return revisions.items.find((row) => row.is_current) ?? revisions.items[0];
  };

  const emptyHeaderBindingOverlay = {
    schema: 'header-binding-overlay/v1',
    aliases: [] as unknown[],
  };

  // Capture profile OCC version BEFORE reading current-revision overlay for the same POST.
  profiles = await loadProfiles();
  const refreshed = profiles.find((row) => row.id === profile!.id) ?? profile;
  const expectedVersion = refreshed.version;

  let current = await loadCurrentRevision(refreshed.id);
  let overlayForUpgrade: Record<string, unknown> = emptyHeaderBindingOverlay;
  if (current !== undefined) {
    const detail = await api.getImportRevision(refreshed.id, current.id);
    if (isMappingSchemaV2(detail.mapping)) {
      return { profile: refreshed, revisionId: current.id };
    }
    overlayForUpgrade =
      detail.header_binding_overlay !== null && detail.header_binding_overlay !== undefined
        ? detail.header_binding_overlay
        : emptyHeaderBindingOverlay;
  }

  try {
    await api.createImportRevision(
      refreshed.id,
      expectedVersion,
      buildAutoF3MappingV2Payload(),
      overlayForUpgrade,
    );
  } catch {
    // race/duplicate — reload and reuse existing current revision below
  }

  profiles = await loadProfiles();
  profile = profiles.find((row) => row.id === refreshed.id) ?? refreshed;
  current = await loadCurrentRevision(profile.id);
  if (current === undefined) {
    throw new Error('Не удалось создать версию настроек AUTO-F3.');
  }
  const detail = await api.getImportRevision(profile.id, current.id);
  if (!isMappingSchemaV2(detail.mapping)) {
    throw new Error('Не удалось создать версию настроек AUTO-F3 v2.');
  }
  return { profile, revisionId: current.id };
}

export function TemplatesAdmin(props: {
  readonly api: AdminApi;
  /**
   * Test-only: open template detail with preferredVersionId (same path as post-publish focus).
   * Production Portal never sets this prop.
   */
  readonly preferredOpenRequest?: {
    readonly templateId: string;
    readonly versionId: string;
    readonly nonce: number;
  } | null;
}) {
  const [tab, setTab] = useState<TemplateTab>('templates');
  const [templates, setTemplates] = useState<ChecklistTemplateAdmin[] | null>(null);
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [builderDraftId, setBuilderDraftId] = useState<string | null>(null);
  const [templatesFocus, setTemplatesFocus] = useState<TemplatesFocus | null>(null);
  const [runtimePreview, setRuntimePreview] = useState<RuntimePreviewTarget | null>(null);
  /** In-memory Templates subview: list search + last detail selection (survives tab/preview unmount). */
  const [listSearch, setListSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [versionsByTemplateId, setVersionsByTemplateId] = useState<
    Readonly<Record<string, readonly TemplateVersionRow[]>>
  >({});
  const [detailLoadState, setDetailLoadState] = useState<'idle' | 'loading' | 'ready' | 'failed'>(
    'idle',
  );
  const [publishNotice, setPublishNotice] = useState<string | null>(null);
  /** Last selected version per template; survives back-to-list so warm reopen restores selection. */
  const [lastVersionByTemplateId, setLastVersionByTemplateId] = useState<
    Readonly<Record<string, string>>
  >({});
  const versionsCacheRef = useRef(versionsByTemplateId);
  versionsCacheRef.current = versionsByTemplateId;
  const templatesRef = useRef(templates);
  templatesRef.current = templates;
  const lastVersionRef = useRef(lastVersionByTemplateId);
  lastVersionRef.current = lastVersionByTemplateId;
  const detailLoadSeqRef = useRef(0);

  const reloadTemplates = () => {
    void props.api
      .listChecklistTemplates()
      .then(setTemplates)
      .catch((failure: unknown) => setError(failure));
  };

  const refreshTemplates = (): Promise<ChecklistTemplateAdmin[]> =>
    props.api.listChecklistTemplates().then((rows) => {
      setTemplates(rows);
      return rows;
    });

  /**
   * Invalidation helper for published version summaries.
   * Pass templateId for one logical template; omit to clear all.
   * Future 027C activation/deactivation should call this for the affected template
   * after reloading the templates catalog.
   */
  const invalidateTemplateVersions = (templateId?: string) => {
    if (templateId === undefined) {
      setVersionsByTemplateId({});
      return;
    }
    setVersionsByTemplateId((current) => {
      if (current[templateId] === undefined) {
        return current;
      }
      const next = { ...current };
      delete next[templateId];
      return next;
    });
  };

  const ensureUsersLoaded = () => {
    if (users !== null || usersLoading) {
      return;
    }
    setUsersLoading(true);
    void props.api
      .listUsers()
      .then((rows) => {
        setUsers(rows);
        setUsersLoading(false);
      })
      .catch((failure: unknown) => {
        setError(failure);
        setUsersLoading(false);
      });
  };

  const onCatalogChanged = (options?: { readonly invalidateVersionsFor?: string | 'all' }) => {
    reloadTemplates();
    if (options?.invalidateVersionsFor === 'all') {
      invalidateTemplateVersions();
    } else if (typeof options?.invalidateVersionsFor === 'string') {
      invalidateTemplateVersions(options.invalidateVersionsFor);
    }
  };

  useEffect(() => {
    reloadTemplates();
  }, [props.api]);

  useEffect(() => {
    if (tab === 'qualifications' || tab === 'assignments') {
      ensureUsersLoaded();
    }
  }, [tab, props.api]);

  useEffect(() => {
    const req = props.preferredOpenRequest;
    if (req === null || req === undefined) {
      return;
    }
    setTab('templates');
    setTemplatesFocus({ templateId: req.templateId, versionId: req.versionId });
  }, [props.preferredOpenRequest]);

  const handlePublished = (result: PublishedTemplateVersionResult) => {
    setBuilderDraftId(null);
    setRuntimePreview(null);
    invalidateTemplateVersions(result.template_id);
    setTemplatesFocus({
      templateId: result.template_id,
      versionId: result.id,
    });
    setTab('templates');
    reloadTemplates();
  };

  const cacheTemplateVersions = (templateId: string, history: readonly TemplateVersionRow[]) => {
    setVersionsByTemplateId((current) => ({ ...current, [templateId]: history }));
  };

  const resolveSelectedVersionId = (
    template: ChecklistTemplateAdmin,
    history: readonly TemplateVersionRow[],
    preferredVersionId?: string | null,
  ): { readonly versionId: string; readonly preferredMatched: boolean } => {
    const preferred =
      preferredVersionId !== undefined &&
      preferredVersionId !== null &&
      history.some((row) => row.id === preferredVersionId)
        ? preferredVersionId
        : null;
    if (preferred !== null) {
      return { versionId: preferred, preferredMatched: true };
    }
    const remembered = lastVersionRef.current[template.id];
    if (remembered !== undefined && history.some((row) => row.id === remembered)) {
      return { versionId: remembered, preferredMatched: false };
    }
    return {
      versionId: template.active_template_version_id ?? history[0]?.id ?? '',
      preferredMatched: false,
    };
  };

  /**
   * Open template detail. Warm path (cache hit): 0 network, never enters 'loading'.
   * Cold path: at most 1× listChecklistTemplateVersions.
   */
  const openTemplateDetail = (id: string, preferredVersionId?: string | null) => {
    const seq = ++detailLoadSeqRef.current;
    setPublishNotice(null);
    setError(null);

    const applyPublishNotice = (
      template: ChecklistTemplateAdmin,
      history: readonly TemplateVersionRow[],
      preferredMatched: boolean,
    ) => {
      if (
        !preferredMatched ||
        preferredVersionId === undefined ||
        preferredVersionId === null ||
        template.is_deleted === true
      ) {
        return;
      }
      const version = history.find((row) => row.id === preferredVersionId);
      setPublishNotice(
        version === undefined
          ? 'Версия сохранена. Чтобы сделать её текущей, выберите «Сделать активной».'
          : `Версия ${version.version_code} сохранена. Чтобы сделать её текущей, выберите «Сделать активной».`,
      );
    };

    const finishOpen = (template: ChecklistTemplateAdmin) => {
      if (seq !== detailLoadSeqRef.current) {
        return;
      }
      const cached = versionsCacheRef.current[id];
      if (cached !== undefined) {
        const resolved = resolveSelectedVersionId(template, cached, preferredVersionId);
        setSelectedId(id);
        setSelectedVersionId(resolved.versionId);
        setDetailLoadState('ready');
        applyPublishNotice(template, cached, resolved.preferredMatched);
        return;
      }
      setSelectedId(id);
      setSelectedVersionId('');
      setDetailLoadState('loading');
      void props.api
        .listChecklistTemplateVersions(id)
        .then((history) => {
          if (seq !== detailLoadSeqRef.current) {
            return;
          }
          cacheTemplateVersions(id, history);
          const resolved = resolveSelectedVersionId(template, history, preferredVersionId);
          setSelectedVersionId(resolved.versionId);
          setDetailLoadState('ready');
          applyPublishNotice(template, history, resolved.preferredMatched);
        })
        .catch((failure: unknown) => {
          if (seq !== detailLoadSeqRef.current) {
            return;
          }
          setSelectedVersionId('');
          setDetailLoadState('failed');
          setError(failure);
        });
    };

    const local = templatesRef.current?.find((row) => row.id === id) ?? null;
    if (local !== null) {
      finishOpen(local);
      return;
    }
    // Soft-deleted (or otherwise absent from working catalog): resolve via get.
    setSelectedId(id);
    setSelectedVersionId('');
    setDetailLoadState('loading');
    void props.api
      .getChecklistTemplate(id)
      .then((template) => finishOpen(template))
      .catch((failure: unknown) => {
        if (seq !== detailLoadSeqRef.current) {
          return;
        }
        setDetailLoadState('failed');
        setError(failure);
      });
  };

  const clearTemplateSelection = () => {
    if (selectedId !== null && selectedVersionId.length > 0) {
      setLastVersionByTemplateId((current) => ({
        ...current,
        [selectedId]: selectedVersionId,
      }));
    }
    setSelectedId(null);
    setSelectedVersionId('');
    setDetailLoadState('idle');
    setPublishNotice(null);
  };

  return (
    <>
      <PageHeader
        title="Чек-листы"
        description="Каталог чек-листов, конструктор версий, импорт Excel, применение версий, допуски и назначения."
      />
      <div className="section-tabs" role="tablist" aria-label="Разделы чек-листов">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={
              tab === item.id
                ? 'section-tab is-active nav-link nav-current'
                : 'section-tab nav-link'
            }
            onClick={() => {
              setRuntimePreview(null);
              setTab(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {runtimePreview !== null && (tab === 'templates' || tab === 'builder') ? (
        <TemplateVersionRuntimePreview
          api={props.api}
          templateVersionId={runtimePreview.versionId}
          templateDisplayName={runtimePreview.templateDisplayName}
          versionCode={runtimePreview.versionCode}
          isActive={runtimePreview.isActive}
          onBack={() => {
            // Preserve lifted list/detail/search/versions cache — no focus reopen/refetch.
            setRuntimePreview(null);
          }}
        />
      ) : null}
      {runtimePreview === null && tab === 'builder' ? (
        <TemplateAuthoringAdmin
          api={props.api}
          templates={templates}
          initialDraftId={builderDraftId}
          onInitialDraftConsumed={() => setBuilderDraftId(null)}
          onPublished={handlePublished}
          onCatalogChanged={() => onCatalogChanged()}
          onOpenRuntimePreview={(target) =>
            setRuntimePreview({ ...target, returnTo: 'detail' })
          }
        />
      ) : null}
      {runtimePreview === null && tab === 'templates' ? (
        <TemplateSection
          api={props.api}
          templates={templates}
          focus={templatesFocus}
          onFocusConsumed={() => setTemplatesFocus(null)}
          onOpenAuthoringDraft={(draftId) => {
            setBuilderDraftId(draftId);
            setTab('builder');
          }}
          onOpenRuntimePreview={setRuntimePreview}
          listSearch={listSearch}
          onListSearchChange={setListSearch}
          selectedId={selectedId}
          selectedVersionId={selectedVersionId}
          onSelectedVersionIdChange={(versionId) => {
            setSelectedVersionId(versionId);
            if (selectedId !== null && versionId.length > 0) {
              setLastVersionByTemplateId((current) => ({
                ...current,
                [selectedId]: versionId,
              }));
            }
          }}
          versionsByTemplateId={versionsByTemplateId}
          detailLoadState={detailLoadState}
          publishNotice={publishNotice}
          onPublishNoticeChange={setPublishNotice}
          onOpenTemplate={openTemplateDetail}
          onClearSelection={clearTemplateSelection}
          onRefreshTemplates={refreshTemplates}
          onActivationSucceeded={(templateId) => {
            void refreshTemplates();
            invalidateTemplateVersions(templateId);
            void props.api.listChecklistTemplateVersions(templateId).then((history) => {
              cacheTemplateVersions(templateId, history);
            });
          }}
        />
      ) : null}
      {runtimePreview === null && tab === 'import' ? (
        <ImportSection
          api={props.api}
          templates={templates ?? []}
          onCatalogChanged={() => onCatalogChanged({ invalidateVersionsFor: 'all' })}
          onOpenAuthoringDraft={(draftId) => {
            setBuilderDraftId(draftId);
            setTab('builder');
          }}
        />
      ) : null}
      {runtimePreview === null && tab === 'qualifications' ? (
        <QualificationSection api={props.api} templates={templates ?? []} users={users ?? []} />
      ) : null}
      {runtimePreview === null && tab === 'assignments' ? (
        <AssignmentSection api={props.api} templates={templates ?? []} users={users ?? []} />
      ) : null}
    </>
  );
}

function TemplateSection(props: {
  readonly api: AdminApi;
  readonly templates: ChecklistTemplateAdmin[] | null;
  readonly onOpenAuthoringDraft: (draftId: string) => void;
  readonly focus: TemplatesFocus | null;
  readonly onFocusConsumed: () => void;
  readonly onOpenRuntimePreview: (target: RuntimePreviewTarget) => void;
  readonly listSearch: string;
  readonly onListSearchChange: (value: string) => void;
  readonly selectedId: string | null;
  readonly selectedVersionId: string;
  readonly onSelectedVersionIdChange: (value: string) => void;
  readonly versionsByTemplateId: Readonly<Record<string, readonly TemplateVersionRow[]>>;
  readonly detailLoadState: 'idle' | 'loading' | 'ready' | 'failed';
  readonly publishNotice: string | null;
  readonly onPublishNoticeChange: (value: string | null) => void;
  readonly onOpenTemplate: (id: string, preferredVersionId?: string | null) => void;
  readonly onClearSelection: () => void;
  readonly onRefreshTemplates: () => Promise<ChecklistTemplateAdmin[]>;
  readonly onActivationSucceeded: (templateId: string) => void;
}) {
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applyPhase, setApplyPhase] = useState<
    'idle' | 'assessing' | 'confirm' | 'activating' | 'needs-reassess'
  >('idle');
  const [assessment, setAssessment] = useState<ConfigurationImpactAssessment | null>(null);
  const [applyHint, setApplyHint] = useState<string | null>(null);
  const [catalogBucket, setCatalogBucket] = useState<'working' | 'deleted'>('working');
  const [deletedTemplates, setDeletedTemplates] = useState<ChecklistTemplateAdmin[] | null>(null);
  const [confirmSoftDelete, setConfirmSoftDelete] = useState<ChecklistTemplateAdmin | null>(null);

  const resetApplySurface = () => {
    setApplyPhase('idle');
    setAssessment(null);
    setApplyHint(null);
  };

  const selectedId = props.selectedId;
  const selectedVersionId = props.selectedVersionId;
  const detailLoadState = props.detailLoadState;
  const publishNotice = props.publishNotice;
  const listSearch = props.listSearch;

  useEffect(() => {
    resetApplySurface();
  }, [selectedId]);

  const reloadDeleted = () => {
    void props.api
      .listDeletedChecklistTemplates()
      .then((rows) => setDeletedTemplates(rows))
      .catch((failure: unknown) => setError(failure));
  };

  useEffect(() => {
    if (catalogBucket !== 'deleted') {
      return;
    }
    setDeletedTemplates(null);
    reloadDeleted();
  }, [catalogBucket, props.api]);

  const detailSource =
    catalogBucket === 'deleted' ? deletedTemplates : props.templates;
  const detail =
    selectedId === null
      ? null
      : (detailSource?.find((row) => row.id === selectedId) ?? null);
  const detailIsDeleted = detail?.is_deleted === true || catalogBucket === 'deleted';

  const versions =
    selectedId === null ? [] : [...(props.versionsByTemplateId[selectedId] ?? [])];

  const publishedTemplates =
    catalogBucket === 'deleted'
      ? deletedTemplates
      : props.templates === null
        ? null
        : props.templates.filter((row) => row.has_approved_version === true);

  const listQuery = listSearch.trim().toLocaleLowerCase('ru-RU');
  const filteredTemplates =
    publishedTemplates === null
      ? null
      : publishedTemplates.filter((item) => {
          if (listQuery.length === 0) {
            return true;
          }
          const primary = templatePrimaryDisplayName(item.name).toLocaleLowerCase('ru-RU');
          const rawName = item.name.toLocaleLowerCase('ru-RU');
          const code = item.template_code.toLocaleLowerCase('ru-RU');
          return primary.includes(listQuery) || rawName.includes(listQuery) || code.includes(listQuery);
        });

  useEffect(() => {
    if (props.focus === null) {
      return;
    }
    setError(null);
    setSuccess(null);
    props.onOpenTemplate(props.focus.templateId, props.focus.versionId);
    props.onFocusConsumed();
  }, [props.focus]);

  const backToList = () => {
    props.onClearSelection();
    setError(null);
    setSuccess(null);
  };

  const templateMatchesSelection =
    detail !== null &&
    selectedId !== null &&
    detail.id === selectedId &&
    detailLoadState === 'ready';

  const selectedVersion =
    templateMatchesSelection && selectedVersionId.length > 0
      ? (versions.find((row) => row.id === selectedVersionId) ?? null)
      : null;

  const mutate = (work: () => Promise<void>) => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    void work()
      .catch((failure: unknown) => setError(failure))
      .finally(() => setBusy(false));
  };

  const createDraftFromSelectedVersion = () => {
    if (!templateMatchesSelection || detail === null || selectedVersion === null) {
      return;
    }
    if (
      selectedVersion.template_id !== undefined &&
      selectedVersion.template_id !== detail.id
    ) {
      setError(new Error('Выбранная версия не принадлежит этому чек-листу.'));
      return;
    }
    if (!versions.some((row) => row.id === selectedVersion.id)) {
      setError(new Error('Выбранная версия отсутствует в истории этого чек-листа.'));
      return;
    }
    mutate(async () => {
      const draft = await props.api.createTemplateAuthoringDraftFromTemplateVersion(
        selectedVersion.id,
        detail.id,
        detail.version,
      );
      setSuccess('Черновик новой версии создан.');
      props.onOpenAuthoringDraft(draft.id);
    });
  };

  if (selectedId !== null) {
    const headingName =
      detail !== null ? templatePrimaryDisplayName(detail.name) : 'Чек-лист';
    return (
      <>
        {error !== null ? <ProblemAlert failure={error} /> : null}
        {success !== null ? (
          <p role="status" className="banner">
            {success}
          </p>
        ) : null}
        {publishNotice !== null ? (
          <p role="status" className="banner">
            {publishNotice}
          </p>
        ) : null}
        <button type="button" className="admin-back-link" onClick={backToList}>
          ← К списку
        </button>
        {detail !== null ? <h2>{headingName}</h2> : null}
        {detailLoadState === 'loading' ? <p role="status">Загрузка версий…</p> : null}
        {detailLoadState === 'failed' ? (
          <p role="status" className="banner">
            Не удалось загрузить чек-лист. Вернитесь к списку и попробуйте снова.
          </p>
        ) : null}
        {templateMatchesSelection && detail !== null ? (
          <>
            {detailIsDeleted ? (
              <p className="meta" role="status">
                Чек-лист в разделе «Удалённые». Просмотр истории версий только для чтения. Активация
                и публикация недоступны, пока запись не восстановлена.
              </p>
            ) : (
              <p>
                {activeVersionLabel(
                  detail.active_template_version_id === null
                    ? null
                    : (versions.find((row) => row.id === detail.active_template_version_id)
                        ?.version_code ??
                        detail.active_version_code ??
                        null),
                )}
              </p>
            )}
            <p className="meta admin-checklist-hierarchy-help">
              В работу берётся активная версия чек-листа. Старые осмотры сохраняют ту версию, с
              которой были созданы.
            </p>
            <h3 className="section-title">Версии чек-листа</h3>
            {versions.length === 0 ? (
              <p role="status" className="banner">
                Опубликованные версии ещё не созданы.
              </p>
            ) : (
              <>
                <ul className="admin-compact-list" aria-label="Версии чек-листа">
                  {versions.map((row) => {
                    const awaitingApply =
                      !row.is_active &&
                      (String(row.status).toUpperCase() === 'APPROVED' ||
                        String(row.status).length === 0);
                    const line = [
                      row.version_code,
                      row.is_active ? 'Активная' : null,
                      awaitingApply ? 'Архивная' : null,
                    ]
                      .filter((part): part is string => part !== null)
                      .join(' · ');
                    return (
                      <li key={row.id} className="admin-compact-row">
                        <button
                          type="button"
                          className={
                            selectedVersionId === row.id
                              ? 'admin-template-list-btn is-selected'
                              : 'admin-template-list-btn'
                          }
                          aria-pressed={selectedVersionId === row.id}
                          onClick={() => {
                            props.onSelectedVersionIdChange(row.id);
                            props.onPublishNoticeChange(null);
                            resetApplySurface();
                          }}
                        >
                          <span className="admin-compact-row-title" title={line}>
                            {line}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {selectedVersion !== null && !selectedVersion.is_active ? (
                  <p className="meta" role="status" title="Не является текущей версией. Сохранена для истории и ранее созданных осмотров.">
                    Архивная
                  </p>
                ) : null}
                {selectedVersion !== null && selectedVersion.is_active === true ? (
                  <p className="meta" role="status">
                    Активная
                  </p>
                ) : null}
                <label htmlFor="activate_version">
                  Выбранная версия
                  <select
                    id="activate_version"
                    value={selectedVersionId}
                    disabled={busy || applyPhase === 'assessing' || applyPhase === 'activating'}
                    onChange={(event) => {
                      props.onSelectedVersionIdChange(event.target.value);
                      props.onPublishNoticeChange(null);
                      resetApplySurface();
                    }}
                  >
                    <option value="">Выберите версию</option>
                    {versions.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.version_code}
                        {!row.is_active ? ' · Архивная' : ' · Активная'}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="actions">
                  <button
                    type="button"
                    className="admin-preview-action"
                    disabled={busy || selectedVersion === null || !templateMatchesSelection}
                    title="Посмотреть"
                    aria-label="Посмотреть"
                    onClick={() => {
                      if (detail === null || selectedVersion === null) {
                        return;
                      }
                      props.onOpenRuntimePreview({
                        templateId: detail.id,
                        templateDisplayName: detail.name,
                        versionId: selectedVersion.id,
                        versionCode: selectedVersion.version_code,
                        isActive: selectedVersion.is_active === true,
                        returnTo: 'detail',
                      });
                    }}
                  >
                    <span aria-hidden="true">👁</span>
                    <span className="admin-preview-action-label">Посмотреть</span>
                  </button>
                  {detailIsDeleted ? (
                    <button
                      type="button"
                      className="authoring-field-icon-btn"
                      title="Восстановить"
                      aria-label="Восстановить"
                      disabled={busy}
                      onClick={() => {
                        mutate(async () => {
                          await props.api.restoreChecklistTemplate(detail.id, {
                            expected_version: detail.version,
                          });
                          setSuccess(
                            `Чек-лист ${detail.template_code} восстановлен. Активная версия не назначена.`,
                          );
                          setCatalogBucket('working');
                          await props.onRefreshTemplates();
                          props.onClearSelection();
                        });
                      }}
                    >
                      <span aria-hidden="true">↺</span>
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={
                          busy ||
                          selectedVersion === null ||
                          !templateMatchesSelection ||
                          applyPhase === 'assessing' ||
                          applyPhase === 'activating'
                        }
                        onClick={createDraftFromSelectedVersion}
                      >
                        Создать новую версию
                      </button>
                      {selectedVersion !== null &&
                      selectedVersion.is_active !== true &&
                      templateMatchesSelection ? (
                        <button
                          type="button"
                          disabled={
                            busy || applyPhase === 'assessing' || applyPhase === 'activating'
                          }
                          onClick={() => {
                            if (detail === null || selectedVersion === null) {
                              return;
                            }
                            setError(null);
                            setSuccess(null);
                            setApplyHint(null);
                            setApplyPhase('assessing');
                            setAssessment(null);
                            void props.api
                              .assessChecklistTemplateActivationImpact(detail.id, {
                                expected_version: detail.version,
                                template_version_id: selectedVersion.id,
                                impact_mode: 'MANDATORY_PRESTART',
                              })
                              .then((row) => {
                                setAssessment(row);
                                setApplyPhase('confirm');
                              })
                              .catch((failure: unknown) => {
                                setApplyPhase('idle');
                                setError(failure);
                              });
                          }}
                        >
                          Сделать активной
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
                {applyPhase === 'assessing' ? (
                  <p className="meta" role="status">
                    Проверяем, какие осмотры затронуты…
                  </p>
                ) : null}
                {applyPhase === 'needs-reassess' ? (
                  <div className="admin-template-apply-impact" role="status">
                    <p>
                      {applyHint ?? 'Данные изменились. Оцените влияние ещё раз.'}
                    </p>
                    <div className="actions">
                      <button
                        type="button"
                        disabled={busy || selectedVersion === null}
                        onClick={() => {
                          if (detail === null || selectedVersion === null) {
                            return;
                          }
                          const versionId = selectedVersion.id;
                          const templateId = detail.id;
                          setError(null);
                          setApplyHint(null);
                          setApplyPhase('assessing');
                          setAssessment(null);
                          void props
                            .onRefreshTemplates()
                            .then((rows) => {
                              const fresh = rows.find((row) => row.id === templateId);
                              if (fresh === undefined) {
                                throw new Error('Чек-лист отсутствует в загруженном каталоге.');
                              }
                              return props.api.assessChecklistTemplateActivationImpact(fresh.id, {
                                expected_version: fresh.version,
                                template_version_id: versionId,
                                impact_mode: 'MANDATORY_PRESTART',
                              });
                            })
                            .then((row) => {
                              setAssessment(row);
                              setApplyPhase('confirm');
                            })
                            .catch((failure: unknown) => {
                              setApplyPhase('needs-reassess');
                              setError(failure);
                            });
                        }}
                      >
                        Повторить оценку
                      </button>
                      <button type="button" onClick={resetApplySurface}>
                        Отмена
                      </button>
                    </div>
                  </div>
                ) : null}
                {(applyPhase === 'confirm' || applyPhase === 'activating') &&
                assessment !== null &&
                selectedVersion !== null ? (
                  <div
                    className="admin-template-apply-impact"
                    role="region"
                    aria-label="Что изменится после применения"
                  >
                    <h3 className="section-title">Что изменится после применения</h3>
                    <p>{TEMPLATE_APPLY_IMPACT_INTRO}</p>
                    <ul className="admin-template-impact-list">
                      {templateActivationImpactRows(assessment).map((row) => (
                        <li
                          key={row.key}
                          className={
                            row.emphasize
                              ? 'admin-template-impact-row is-emphasized'
                              : 'admin-template-impact-row'
                          }
                        >
                          <p className="admin-template-impact-title">
                            {row.title}
                            <span className="admin-template-impact-count"> · {row.count}</span>
                          </p>
                          <p className="meta">{row.explanation}</p>
                        </li>
                      ))}
                    </ul>
                    {assessment.counts.unacknowledged_devices > 0 ? (
                      <p className="meta">
                        Устройства без подтверждения загрузки:{' '}
                        {assessment.counts.unacknowledged_devices}
                      </p>
                    ) : null}
                    {applyPhase === 'activating' ? (
                      <p className="meta" role="status">
                        Применяем изменения…
                      </p>
                    ) : null}
                    <div className="actions">
                      <button
                        type="button"
                        disabled={applyPhase === 'activating' || busy}
                        onClick={() => {
                          if (detail === null || selectedVersion === null || assessment === null) {
                            return;
                          }
                          setApplyPhase('activating');
                          setError(null);
                          void props.api
                            .activateChecklistTemplateVersion(detail.id, {
                              expected_version: assessment.expected_root_version,
                              template_version_id: selectedVersion.id,
                              impact_assessment_id: assessment.id,
                              impact_mode: 'MANDATORY_PRESTART',
                            })
                            .then(() => {
                              resetApplySurface();
                              setSuccess(TEMPLATE_APPLY_SUCCESS_NOTICE);
                              props.onActivationSucceeded(detail.id);
                            })
                            .catch((failure: unknown) => {
                              const code =
                                failure instanceof ApiError ? failure.problem.code : undefined;
                              if (
                                code === 'STALE_VERSION' ||
                                code === 'INVALID_ACTIVATION_TARGET'
                              ) {
                                setAssessment(null);
                                setApplyPhase('needs-reassess');
                                setApplyHint(
                                  code === 'STALE_VERSION'
                                    ? 'Чек-лист изменился в другой сессии. Данные обновлены — повторите применение версии.'
                                    : 'Данные изменились. Оцените влияние ещё раз.',
                                );
                                void props.onRefreshTemplates();
                                return;
                              }
                              setApplyPhase('confirm');
                              setError(failure);
                            });
                        }}
                      >
                        Применить изменения
                      </button>
                      <button
                        type="button"
                        disabled={applyPhase === 'activating'}
                        onClick={resetApplySurface}
                      >
                        Отмена
                      </button>
                    </div>
                    <p className="meta">{TEMPLATE_APPLY_IMPACT_INTRO}</p>
                  </div>
                ) : null}
              </>
            )}
            <TechnicalDetails>
              <p>Код чек-листа: {detail.template_code}</p>
              <p>Полное название: {detail.name}</p>
              <p>Идентификатор чек-листа: {detail.id}</p>
              <p>Оптимистичная версия: {detail.version}</p>
              {detail.active_template_version_id !== null ? (
                <p>Идентификатор активной версии: {detail.active_template_version_id}</p>
              ) : null}
            </TechnicalDetails>
          </>
        ) : null}
      </>
    );
  }

  return (
    <>
      <p className="meta admin-checklist-hierarchy-help">
        Тип спецтехники использует пакет чек-листов. Каждый чек-лист хранит свои версии. При
        применении новой версии привязка чек-листа к пакету сохраняется.
      </p>
      {selectedId === null ? (
        <div className="section-tabs" role="tablist" aria-label="Каталог чек-листов">
          <button
            type="button"
            role="tab"
            aria-selected={catalogBucket === 'working'}
            className={catalogBucket === 'working' ? 'is-active' : undefined}
            onClick={() => {
              setCatalogBucket('working');
              setConfirmSoftDelete(null);
              setError(null);
              setSuccess(null);
            }}
          >
            Рабочие
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={catalogBucket === 'deleted'}
            className={catalogBucket === 'deleted' ? 'is-active' : undefined}
            onClick={() => {
              setCatalogBucket('deleted');
              setConfirmSoftDelete(null);
              props.onClearSelection();
              setError(null);
              setSuccess(null);
            }}
          >
            Удалённые
          </button>
        </div>
      ) : null}
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {success !== null ? (
        <p role="status" className="banner">
          {success}
        </p>
      ) : null}
      {confirmSoftDelete !== null ? (
        <div className="info-card stack-gap" role="alertdialog" aria-labelledby="soft-delete-title">
          <h3 id="soft-delete-title" className="section-title">
            Переместить в «Удалённые»?
          </h3>
          <p>
            Чек-лист «{templatePrimaryDisplayName(confirmSoftDelete.name)}» (
            {confirmSoftDelete.template_code}) будет скрыт из рабочих каталогов. Все версии
            сохраняются физически. Восстановление не выполняет автоматическую активацию версии и
            пакета.
          </p>
          <div className="actions">
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Подтвердить перемещение в удалённые"
              aria-label="Подтвердить перемещение в удалённые"
              disabled={busy}
              onClick={() => {
                const target = confirmSoftDelete;
                mutate(async () => {
                  await props.api.softDeleteChecklistTemplate(target.id, {
                    expected_version: target.version,
                  });
                  setConfirmSoftDelete(null);
                  setSuccess(`Чек-лист ${target.template_code} перемещён в «Удалённые».`);
                  await props.onRefreshTemplates();
                  if (catalogBucket === 'deleted') {
                    reloadDeleted();
                  }
                });
              }}
            >
              <span aria-hidden="true">✓</span>
            </button>
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Отменить"
              aria-label="Отменить"
              disabled={busy}
              onClick={() => setConfirmSoftDelete(null)}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        </div>
      ) : null}
      {selectedId === null && (props.templates === null || publishedTemplates === null) ? (
        <p role="status">Загрузка…</p>
      ) : null}
      {selectedId === null &&
      publishedTemplates !== null &&
      publishedTemplates.length === 0 &&
      catalogBucket === 'working' ? (
        <p role="status" className="banner">
          Опубликованные чек-листы ещё не созданы. Новые чек-листы создаются в конструкторе.
        </p>
      ) : null}
      {selectedId === null &&
      publishedTemplates !== null &&
      publishedTemplates.length === 0 &&
      catalogBucket === 'deleted' ? (
        <p role="status" className="banner">
          В разделе «Удалённые» пока нет чек-листов.
        </p>
      ) : null}
      {selectedId === null && publishedTemplates !== null && publishedTemplates.length > 0 ? (
        <>
          {catalogBucket === 'deleted' ? (
            <h3 className="section-title">Удалённые</h3>
          ) : null}
          <label htmlFor="tpl_list_search">
            Поиск
            <input
              id="tpl_list_search"
              type="search"
              value={listSearch}
              placeholder="Поиск по названию чек-листа"
              onChange={(event) => props.onListSearchChange(event.target.value)}
            />
          </label>
          {filteredTemplates !== null && filteredTemplates.length === 0 ? (
            <p role="status" className="banner">
              Нет чек-листов по текущему поиску.
            </p>
          ) : null}
          {filteredTemplates !== null && filteredTemplates.length > 0 ? (
            <ul
              className="admin-compact-list"
              aria-label={catalogBucket === 'deleted' ? 'Удалённые чек-листы' : 'Чек-листы'}
            >
              {filteredTemplates.map((item) => {
                const primary = templatePrimaryDisplayName(item.name);
                const activeCode =
                  item.active_template_version_id === null ? null : item.active_version_code;
                const status =
                  catalogBucket === 'deleted'
                    ? 'В разделе «Удалённые» · без активной версии'
                    : activeVersionLabel(activeCode);
                const hasActive = item.active_template_version_id !== null;
                return (
                  <li key={item.id} className="admin-compact-row">
                    <div className="admin-compact-row-main">
                      <button
                        type="button"
                        className="admin-template-list-btn"
                        onClick={() => {
                          setError(null);
                          setSuccess(null);
                          props.onOpenTemplate(item.id);
                        }}
                      >
                        <span className="admin-compact-row-text">
                          <span className="admin-compact-row-title" title={primary}>
                            {primary}
                          </span>
                          <span className="admin-compact-row-meta" title={status}>
                            {status}
                          </span>
                        </span>
                        <span className="admin-compact-chevron" aria-hidden="true">
                          ›
                        </span>
                      </button>
                      {catalogBucket === 'working' ? (
                        <>
                          <button
                            type="button"
                            className="authoring-field-icon-btn admin-preview-icon"
                            disabled={!hasActive}
                            title={
                              hasActive
                                ? 'Посмотреть активную версию'
                                : 'Нет активной версии'
                            }
                            aria-label={
                              hasActive
                                ? 'Посмотреть активную версию'
                                : 'Нет активной версии'
                            }
                            onClick={() => {
                              if (
                                item.active_template_version_id === null ||
                                activeCode === null
                              ) {
                                return;
                              }
                              props.onOpenRuntimePreview({
                                templateId: item.id,
                                templateDisplayName: item.name,
                                versionId: item.active_template_version_id,
                                versionCode: activeCode,
                                isActive: true,
                                returnTo: 'list',
                              });
                            }}
                          >
                            <span aria-hidden="true">👁</span>
                          </button>
                          <button
                            type="button"
                            className="authoring-field-icon-btn"
                            title="Переместить в удалённые"
                            aria-label="Переместить в удалённые"
                            disabled={busy}
                            onClick={() => {
                              setConfirmSoftDelete(item);
                              setError(null);
                              setSuccess(null);
                            }}
                          >
                            <span aria-hidden="true">🗑</span>
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="authoring-field-icon-btn"
                          title="Восстановить"
                          aria-label="Восстановить"
                          disabled={busy}
                          onClick={() => {
                            mutate(async () => {
                              await props.api.restoreChecklistTemplate(item.id, {
                                expected_version: item.version,
                              });
                              setSuccess(
                                `Чек-лист ${item.template_code} восстановлен. Активная версия не назначена.`,
                              );
                              reloadDeleted();
                              await props.onRefreshTemplates();
                            });
                          }}
                        >
                          <span aria-hidden="true">↺</span>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function ImportSection(props: {
  readonly api: AdminApi;
  readonly templates: readonly ChecklistTemplateAdmin[];
  readonly onCatalogChanged: () => void;
  readonly onOpenAuthoringDraft: (draftId: string) => void;
}) {
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [clientProblems, setClientProblems] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<TemplateImportProfile[] | null>(null);
  const [profileCode, setProfileCode] = useState('');
  const [profileFamily, setProfileFamily] = useState('F3');
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<readonly ImportRevisionRow[]>([]);
  const [selectedRevisionId, setSelectedRevisionId] = useState<string>('');
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [mappingLoadState, setMappingLoadState] = useState<
    'idle' | 'loading' | 'ready' | 'invalid'
  >('idle');
  const [loadedRevisionId, setLoadedRevisionId] = useState<string | null>(null);
  const profileLoadSeq = useRef(0);
  const [mapping, setMapping] = useState<AnyDeclarativeMapping>(defaultMappingForFamily('F3'));
  const [artifact, setArtifact] = useState<TemplateSourceArtifact | null>(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [destinationMode, setDestinationMode] = useState<'existing' | 'create'>('existing');
  const [run, setRun] = useState<TemplateImportRun | null>(null);
  const [normalRun, setNormalRun] = useState<TemplateImportRun | null>(null);
  const [correction, setCorrection] = useState<CorrectionDraft>(emptyCorrectionDraft());
  const [autoF3Profile, setAutoF3Profile] = useState<TemplateImportProfile | null>(null);
  const [authoringDraftId, setAuthoringDraftId] = useState<string | null>(null);
  const [authoringDraftError, setAuthoringDraftError] = useState<string | null>(null);

  const selectedProfile = profiles?.find((row) => row.id === selectedProfileId) ?? null;
  const selectedTemplate = props.templates.find((row) => row.id === selectedTemplateId) ?? null;
  const selectedRevision = revisions.find((row) => row.id === selectedRevisionId) ?? null;
  const revisionBelongsToSelectedProfile =
    selectedRevisionId.length > 0 && revisions.some((row) => row.id === selectedRevisionId);
  const canCreateRun =
    !busy &&
    !revisionsLoading &&
    artifact !== null &&
    selectedSheet.length > 0 &&
    selectedTemplateId.length > 0 &&
    revisionBelongsToSelectedProfile;
  const canCreateRevision =
    !busy &&
    selectedProfile !== null &&
    mappingLoadState === 'ready' &&
    (revisions.length === 0 || loadedRevisionId === selectedRevisionId);

  const canCreateNormalRun =
    !busy &&
    artifact !== null &&
    selectedSheet.length > 0 &&
    (destinationMode === 'create' || selectedTemplateId.length > 0);

  const resetRunContext = () => {
    setRun(null);
    setNormalRun(null);
    setCorrection(emptyCorrectionDraft());
    setAuthoringDraftId(null);
    setAuthoringDraftError(null);
  };

  const createAuthoringDraftFromRun = async (
    importRun: TemplateImportRun,
  ): Promise<string | null> => {
    if (!canOpenImportRunInBuilder(importRun)) {
      return null;
    }
    try {
      const draft = await props.api.createTemplateAuthoringDraftFromImportRun(
        importRun.id,
        importRun.version,
      );
      setAuthoringDraftId(draft.id);
      setAuthoringDraftError(null);
      props.onCatalogChanged();
      return draft.id;
    } catch (failure: unknown) {
      setAuthoringDraftError(problemMessage(failure));
      return null;
    }
  };

  const reloadProfiles = () => {
    void props.api
      .listImportProfiles()
      .then(setProfiles)
      .catch((failure: unknown) => setError(failure));
  };

  useEffect(() => {
    reloadProfiles();
  }, [props.api]);

  const mutate = (work: () => Promise<void>) => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    setClientProblems([]);
    void work()
      .catch((failure: unknown) => setError(failure))
      .finally(() => setBusy(false));
  };

  const applyRevisionMapping = (seq: number, revisionId: string, raw: unknown) => {
    if (seq !== profileLoadSeq.current) {
      return;
    }
    const parsed = mappingFromUnknown(raw);
    if (parsed.ok) {
      setMapping(parsed.mapping);
      setLoadedRevisionId(revisionId);
      setMappingLoadState('ready');
      setClientProblems([]);
      return;
    }
    setLoadedRevisionId(revisionId);
    setMappingLoadState('invalid');
    setClientProblems(parsed.failures.map((item) => item.message));
  };

  const openProfile = (profileId: string, knownProfile?: TemplateImportProfile) => {
    const profile = knownProfile ?? profiles?.find((row) => row.id === profileId);
    const seq = profileLoadSeq.current + 1;
    profileLoadSeq.current = seq;
    setSelectedProfileId(profileId);
    setError(null);
    setClientProblems([]);
    setRevisions([]);
    setSelectedRevisionId('');
    setLoadedRevisionId(null);
    setRevisionsLoading(true);
    setMappingLoadState('loading');
    setMapping(defaultMappingForFamily(profile?.source_family ?? 'GENERIC'));
    resetRunContext();
    void props.api
      .listImportRevisions(profileId)
      .then(async (body) => {
        if (seq !== profileLoadSeq.current) {
          return;
        }
        setRevisions(body.items);
        setRevisionsLoading(false);
        const current = body.items.find((row) => row.is_current) ?? body.items[0];
        if (current === undefined) {
          setSelectedRevisionId('');
          setLoadedRevisionId(null);
          setMapping(defaultMappingForFamily(profile?.source_family ?? 'GENERIC'));
          setMappingLoadState('ready');
          return;
        }
        setSelectedRevisionId(current.id);
        const detail = await props.api.getImportRevision(profileId, current.id);
        applyRevisionMapping(seq, current.id, detail.mapping);
      })
      .catch((failure: unknown) => {
        if (seq !== profileLoadSeq.current) {
          return;
        }
        setRevisionsLoading(false);
        setMappingLoadState('invalid');
        setError(failure);
      });
  };

  const correctionPreview = buildCorrectionPayload(correction);

  return (
    <>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {success !== null ? (
        <p role="status" className="banner">
          {success}
        </p>
      ) : null}
      {clientProblems.length > 0 ? (
        <div role="alert" className="banner banner-closed">
          {clientProblems.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </div>
      ) : null}

      <h2>Импорт Excel</h2>
      <section className="stack-gap" aria-label="Обычный импорт Excel">
        <h3>1. Выберите Excel-файл</h3>
        <label htmlFor="xlsx_file_normal">
          Выберите Excel-файл
          <input
            id="xlsx_file_normal"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file === undefined) {
                return;
              }
              mutate(async () => {
                const uploaded = await props.api.uploadTemplateSource(file);
                setArtifact(uploaded);
                setSelectedSheet(uploaded.discovered_sheets[0] ?? '');
                resetRunContext();
                setSuccess(
                  `Загружен файл ${normalizeLogicalFilename(uploaded.original_filename)}.`,
                );
              });
            }}
          />
        </label>
        {artifact !== null ? (
          <p>
            Файл: <strong>{normalizeLogicalFilename(artifact.original_filename)}</strong>
            {artifact.discovered_sheets.length > 0
              ? ` · листов: ${artifact.discovered_sheets.length} (${artifact.discovered_sheets.join(', ')})`
              : ''}
          </p>
        ) : null}

        <h3>2. Выберите лист</h3>
        <p className="meta">
          Листы импортируются по одному. После завершения можно выбрать следующий лист этого же
          файла.
        </p>
        <label htmlFor="source_sheet_normal">
          Лист для импорта
          <select
            id="source_sheet_normal"
            value={selectedSheet}
            disabled={busy || artifact === null}
            onChange={(event) => {
              setSelectedSheet(event.target.value);
              resetRunContext();
            }}
          >
            <option value="">Выберите лист</option>
            {(artifact?.discovered_sheets ?? []).map((sheet) => (
              <option key={sheet} value={sheet}>
                {sheet}
              </option>
            ))}
          </select>
        </label>

        <h3>3. Куда импортировать</h3>
        <fieldset>
          <legend>Куда импортировать</legend>
          <label className="admin-choice" htmlFor="import_dest_existing">
            <input
              id="import_dest_existing"
              className="admin-choice__control"
              type="radio"
              name="import_destination"
              checked={destinationMode === 'existing'}
              disabled={busy}
              onChange={() => {
                setDestinationMode('existing');
                resetRunContext();
              }}
            />
            Существующий чек-лист
          </label>
          <label className="admin-choice" htmlFor="import_dest_create">
            <input
              id="import_dest_create"
              className="admin-choice__control"
              type="radio"
              name="import_destination"
              checked={destinationMode === 'create'}
              disabled={busy}
              onChange={() => {
                setDestinationMode('create');
                setSelectedTemplateId('');
                resetRunContext();
              }}
            />
            Создать новый чек-лист из этого листа
          </label>
        </fieldset>
        {destinationMode === 'existing' ? (
          <label htmlFor="run_template_normal">
            Существующий чек-лист
            <select
              id="run_template_normal"
              value={selectedTemplateId}
              disabled={busy}
              onChange={(event) => {
                setSelectedTemplateId(event.target.value);
                resetRunContext();
              }}
            >
              <option value="">Выберите чек-лист</option>
              {props.templates.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ({item.template_code})
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="meta">
            Будет создан чек-лист с названием листа
            {selectedSheet.length > 0 ? ` «${selectedSheet}»` : ''}. Внутренний код система задаст
            сама.
          </p>
        )}

        <button
          type="button"
          disabled={!canCreateNormalRun}
          onClick={() => {
            if (artifact === null) {
              return;
            }
            mutate(async () => {
              const { profile, revisionId } = await resolveAutoF3Revision(props.api);
              setAutoF3Profile(profile);
              let templateId = selectedTemplateId;
              if (destinationMode === 'create') {
                const createdTemplate = await props.api.createChecklistTemplate(
                  generateInternalCode('ChecklistTemplate'),
                  selectedSheet,
                );
                templateId = createdTemplate.id;
                setSelectedTemplateId(createdTemplate.id);
                props.onCatalogChanged();
              }
              const created = await props.api.createImportRun({
                source_artifact_id: artifact.id,
                source_sheet: selectedSheet,
                import_profile_revision_id: revisionId,
                checklist_template_id: templateId,
              });
              setNormalRun(created);
              setRun(null);
              setCorrection(emptyCorrectionDraft());
              setAuthoringDraftId(null);
              setAuthoringDraftError(null);
              const draftId = await createAuthoringDraftFromRun(created);
              if (draftId !== null) {
                setSuccess(
                  `Импорт выполнен. Статус: ${importRunStatusLabel(created.status)}. Черновик конструктора создан.`,
                );
              } else if (canOpenImportRunInBuilder(created)) {
                setSuccess(null);
                setError(
                  new Error(
                    `Импорт выполнен (${importRunStatusLabel(created.status)}), но черновик конструктора не создан. Можно повторить создание без повторной загрузки Excel.`,
                  ),
                );
              } else {
                setSuccess(`Импорт выполнен. Статус: ${importRunStatusLabel(created.status)}.`);
              }
            });
          }}
        >
          Импортировать лист
        </button>

        {normalRun !== null ? (
          <ImportRunPanel
            mode="normal"
            run={normalRun}
            busy={busy}
            correction={correction}
            sourceFamily={autoF3Profile?.source_family ?? AUTO_F3_FAMILY}
            onCorrectionChange={setCorrection}
            correctionPreview={correctionPreview}
            onApplyCorrection={() => undefined}
            authoringDraftId={authoringDraftId}
            authoringDraftError={authoringDraftError}
            onOpenAuthoringDraft={() => {
              if (authoringDraftId === null) {
                return;
              }
              props.onOpenAuthoringDraft(authoringDraftId);
            }}
            onRetryCreateAuthoringDraft={() => {
              if (authoringDraftId !== null) {
                return;
              }
              mutate(async () => {
                const draftId = await createAuthoringDraftFromRun(normalRun);
                if (draftId !== null) {
                  setSuccess(
                    'Черновик конструктора создан. Публикация и активация выполняются отдельно.',
                  );
                  setError(null);
                } else if (canOpenImportRunInBuilder(normalRun)) {
                  setSuccess(null);
                  setError(
                    new Error(
                      'Черновик конструктора не создан. Повторите без повторной загрузки Excel.',
                    ),
                  );
                }
              });
            }}
          />
        ) : null}
      </section>

      <details className="advanced-import">
        <summary>Расширенные настройки импорта</summary>

        <h2>Профили импорта</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            mutate(async () => {
              const created = await props.api.createImportProfile(
                profileCode.trim(),
                profileFamily.trim(),
              );
              setProfileCode('');
              const fresh = await props.api.listImportProfiles();
              setProfiles(fresh);
              setSuccess(`Профиль ${created.code} создан.`);
              openProfile(created.id, created);
            });
          }}
        >
          <label htmlFor="imp_code">
            Код профиля
            <input
              id="imp_code"
              value={profileCode}
              disabled={busy}
              onChange={(event) => setProfileCode(event.target.value)}
            />
          </label>
          <label htmlFor="imp_family">
            Семейство источника
            <input
              id="imp_family"
              value={profileFamily}
              disabled={busy}
              onChange={(event) => setProfileFamily(event.target.value)}
            />
          </label>
          <button type="submit" disabled={busy || profileCode.trim().length === 0}>
            Создать профиль
          </button>
        </form>
        {profiles === null ? <p role="status">Загрузка…</p> : null}
        {profiles !== null && profiles.length === 0 ? (
          <p role="status" className="banner">
            Профили импорта ещё не созданы.
          </p>
        ) : null}
        {profiles !== null && profiles.length > 0 ? (
          <label htmlFor="import_profile">
            Профиль
            <select
              id="import_profile"
              value={selectedProfileId ?? ''}
              disabled={busy}
              onChange={(event) => {
                if (event.target.value.length > 0) {
                  openProfile(event.target.value);
                }
              }}
            >
              <option value="">Выберите профиль</option>
              {profiles.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code} · {item.source_family}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {selectedProfile !== null ? (
          <>
            <p>
              Текущая версия настроек импорта:{' '}
              {selectedRevision === null
                ? 'ещё не создана'
                : `№ ${selectedRevision.revision_no}${selectedRevision.is_current ? ' · текущая' : ''}`}{' '}
              · {profileStatusLabel(selectedProfile.status)}
            </p>
            {revisions.length === 0 ? (
              <p role="status" className="banner">
                Версии настроек ещё не созданы. Задайте сопоставление колонок и сохраните первую
                версию настроек.
              </p>
            ) : (
              <label htmlFor="import_revision">
                Версия настроек импорта
                <select
                  id="import_revision"
                  value={selectedRevisionId}
                  disabled={busy}
                  onChange={(event) => {
                    const revisionId = event.target.value;
                    const seq = profileLoadSeq.current + 1;
                    profileLoadSeq.current = seq;
                    setSelectedRevisionId(revisionId);
                    setLoadedRevisionId(null);
                    setMappingLoadState('loading');
                    setClientProblems([]);
                    resetRunContext();
                    void props.api
                      .getImportRevision(selectedProfile.id, revisionId)
                      .then((detail) => applyRevisionMapping(seq, revisionId, detail.mapping))
                      .catch((failure: unknown) => {
                        if (seq !== profileLoadSeq.current) {
                          return;
                        }
                        setMappingLoadState('invalid');
                        setLoadedRevisionId(revisionId);
                        setError(failure);
                      });
                  }}
                >
                  {revisions.map((row) => (
                    <option key={row.id} value={row.id}>
                      Версия настроек {row.revision_no}
                      {row.is_current ? ' · текущая' : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <h3>Настройка колонок</h3>
            {mappingLoadState === 'loading' ? (
              <p role="status">Загрузка настройки колонок…</p>
            ) : null}
            {mappingLoadState === 'invalid' ? (
              <p role="alert" className="banner banner-closed">
                Настройку колонок выбранной версии прочитать нельзя. Сохранение новой версии
                настроек заблокировано, пока не будет доступна корректная настройка.
              </p>
            ) : null}
            {mappingLoadState === 'ready' ? (
              isDeclarativeMappingV2(mapping) ? (
                <MappingEditorV2 mapping={mapping} disabled={busy} onChange={setMapping} />
              ) : (
                <MappingEditor mapping={mapping} disabled={busy} onChange={setMapping} />
              )
            ) : null}
            <button
              type="button"
              disabled={!canCreateRevision}
              onClick={() => {
                const payload = buildMappingPayload(mapping);
                const problems = [
                  ...validateMappingAny(payload),
                  ...validateGovernedSourceFamilyMapping(selectedProfile.source_family, payload),
                ];
                if (problems.length > 0) {
                  setClientProblems(problems.map((item) => item.message));
                  return;
                }
                mutate(async () => {
                  const listed = await props.api.listImportRevisions(selectedProfile.id);
                  const currentRevision = listed.items.find((row) => row.is_current);
                  let overlay: Record<string, unknown> = {
                    schema: 'header-binding-overlay/v1',
                    aliases: [],
                  };
                  if (currentRevision !== undefined) {
                    const detail = await props.api.getImportRevision(
                      selectedProfile.id,
                      currentRevision.id,
                    );
                    if (
                      detail.header_binding_overlay !== null &&
                      detail.header_binding_overlay !== undefined
                    ) {
                      overlay = detail.header_binding_overlay;
                    }
                  }
                  await props.api.createImportRevision(
                    selectedProfile.id,
                    selectedProfile.version,
                    payload,
                    overlay,
                  );
                  const [freshProfiles, freshRevisions] = await Promise.all([
                    props.api.listImportProfiles(),
                    props.api.listImportRevisions(selectedProfile.id),
                  ]);
                  setProfiles(freshProfiles);
                  setRevisions(freshRevisions.items);
                  const current = freshRevisions.items.find((row) => row.is_current);
                  setSelectedRevisionId(current?.id ?? '');
                  setSuccess(
                    current === undefined
                      ? 'Версия настроек сохранена.'
                      : `Сохранена версия настроек № ${current.revision_no}.`,
                  );
                });
              }}
            >
              Сохранить новую версию настроек
            </button>
            {mappingLoadState === 'ready' ? (
              <details className="technical">
                <summary>Сформированное сопоставление</summary>
                <pre>{JSON.stringify(buildMappingPayload(mapping), null, 2)}</pre>
              </details>
            ) : null}
            <TechnicalDetails>
              <p>Идентификатор профиля: {selectedProfile.id}</p>
              {selectedRevision !== undefined && selectedRevision !== null ? (
                <>
                  <p>Идентификатор версии настроек: {selectedRevision.id}</p>
                  <p>Контрольная сумма версии настроек: {selectedRevision.checksum}</p>
                </>
              ) : null}
            </TechnicalDetails>
          </>
        ) : null}

        <h2>Исходный файл</h2>
        <label htmlFor="xlsx_file">
          Файл .xlsx
          <input
            id="xlsx_file"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file === undefined) {
                return;
              }
              mutate(async () => {
                const uploaded = await props.api.uploadTemplateSource(file);
                setArtifact(uploaded);
                setSelectedSheet(uploaded.discovered_sheets[0] ?? '');
                resetRunContext();
                setSuccess(
                  `Загружен файл ${normalizeLogicalFilename(uploaded.original_filename)}.`,
                );
              });
            }}
          />
        </label>
        {artifact !== null ? (
          <>
            <p>
              Загруженный файл:{' '}
              <strong>{normalizeLogicalFilename(artifact.original_filename)}</strong> ·{' '}
              {artifact.byte_size} байт
            </p>
            <label htmlFor="source_sheet">
              Лист книги
              <select
                id="source_sheet"
                value={selectedSheet}
                disabled={busy}
                onChange={(event) => {
                  setSelectedSheet(event.target.value);
                  resetRunContext();
                }}
              >
                <option value="">Выберите лист</option>
                {artifact.discovered_sheets.map((sheet) => (
                  <option key={sheet} value={sheet}>
                    {sheet}
                  </option>
                ))}
              </select>
            </label>
            <TechnicalDetails>
              <p>Идентификатор исходника: {artifact.id}</p>
              <p>SHA-256: {artifact.sha256}</p>
              <p>Тип: {artifact.media_type}</p>
            </TechnicalDetails>
          </>
        ) : null}

        <h2>Результат импорта</h2>
        <label htmlFor="run_template">
          Чек-лист
          <select
            id="run_template"
            value={selectedTemplateId}
            disabled={busy}
            onChange={(event) => {
              setSelectedTemplateId(event.target.value);
              resetRunContext();
            }}
          >
            <option value="">Выберите чек-лист</option>
            {props.templates.map((item) => (
              <option key={item.id} value={item.id}>
                {templateBusinessLabel(item)}
              </option>
            ))}
          </select>
        </label>
        <p className="meta">
          Выбрано:{' '}
          {artifact === null
            ? 'файл не загружен'
            : normalizeLogicalFilename(artifact.original_filename)}
          {selectedSheet.length > 0 ? ` · лист ${selectedSheet}` : ''}
          {selectedProfile !== null ? ` · профиль ${selectedProfile.code}` : ''}
          {selectedRevision !== null ? ` · версия настроек ${selectedRevision.revision_no}` : ''}
          {selectedTemplate !== undefined && selectedTemplate !== null
            ? ` · ${templateBusinessLabel(selectedTemplate)}`
            : ''}
        </p>
        <button
          type="button"
          disabled={!canCreateRun}
          onClick={() => {
            if (artifact === null) {
              return;
            }
            mutate(async () => {
              const created = await props.api.createImportRun({
                source_artifact_id: artifact.id,
                source_sheet: selectedSheet,
                import_profile_revision_id: selectedRevisionId,
                checklist_template_id: selectedTemplateId,
              });
              setRun(created);
              setNormalRun(null);
              setCorrection(emptyCorrectionDraft());
              setAuthoringDraftId(null);
              setAuthoringDraftError(null);
              const draftId = await createAuthoringDraftFromRun(created);
              if (draftId !== null) {
                setSuccess(
                  `Импорт выполнен. Статус: ${importRunStatusLabel(created.status)}. Черновик конструктора создан.`,
                );
              } else if (canOpenImportRunInBuilder(created)) {
                setSuccess(null);
                setError(
                  new Error(
                    `Импорт выполнен (${importRunStatusLabel(created.status)}), но черновик конструктора не создан. Можно повторить создание без повторной загрузки Excel.`,
                  ),
                );
              } else {
                setSuccess(`Импорт выполнен. Статус: ${importRunStatusLabel(created.status)}.`);
              }
            });
          }}
        >
          Импортировать лист
        </button>

        {run !== null ? (
          <ImportRunPanel
            mode="advanced"
            run={run}
            busy={busy}
            correction={correction}
            sourceFamily={selectedProfile?.source_family ?? ''}
            onCorrectionChange={setCorrection}
            correctionPreview={correctionPreview}
            onApplyCorrection={() => {
              if (!correctionPreview.ok) {
                setClientProblems(correctionPreview.failures.map((item) => item.message));
                return;
              }
              const governed = validateGovernedSourceFamilyCorrection(
                selectedProfile?.source_family ?? '',
                correctionPreview.correction.overlay,
              );
              if (governed.length > 0) {
                setClientProblems(governed.map((item) => item.message));
                return;
              }
              mutate(async () => {
                const updated = await props.api.correctImportRun(
                  run.id,
                  run.version,
                  correctionPreview.correction,
                );
                setRun(updated);
                setCorrection(emptyCorrectionDraft());
                let draftId = authoringDraftId;
                if (draftId === null && canOpenImportRunInBuilder(updated)) {
                  draftId = await createAuthoringDraftFromRun(updated);
                }
                if (draftId !== null) {
                  setSuccess(
                    `Коррекция применена. Статус: ${importRunStatusLabel(updated.status)}. Черновик конструктора создан.`,
                  );
                } else if (canOpenImportRunInBuilder(updated)) {
                  setSuccess(null);
                  setError(
                    new Error(
                      `Коррекция применена (${importRunStatusLabel(updated.status)}), но черновик конструктора не создан. Можно повторить создание без повторной загрузки Excel.`,
                    ),
                  );
                } else {
                  setSuccess(
                    `Коррекция применена. Статус: ${importRunStatusLabel(updated.status)}.`,
                  );
                }
              });
            }}
            authoringDraftId={authoringDraftId}
            authoringDraftError={authoringDraftError}
            onOpenAuthoringDraft={() => {
              if (authoringDraftId === null) {
                return;
              }
              props.onOpenAuthoringDraft(authoringDraftId);
            }}
            onRetryCreateAuthoringDraft={() => {
              if (authoringDraftId !== null) {
                return;
              }
              mutate(async () => {
                const draftId = await createAuthoringDraftFromRun(run);
                if (draftId !== null) {
                  setSuccess(
                    'Черновик из Excel создан. Публикация и применение версии выполняются отдельно.',
                  );
                  setError(null);
                } else if (canOpenImportRunInBuilder(run)) {
                  setSuccess(null);
                  setError(
                    new Error(
                      'Черновик конструктора не создан. Повторите без повторной загрузки Excel.',
                    ),
                  );
                }
              });
            }}
          />
        ) : null}
      </details>
    </>
  );
}

function ImportRunPanel(props: {
  readonly mode?: 'normal' | 'advanced';
  readonly run: TemplateImportRun;
  readonly busy: boolean;
  readonly correction: CorrectionDraft;
  readonly sourceFamily: string;
  readonly correctionPreview: ReturnType<typeof buildCorrectionPayload>;
  readonly onCorrectionChange: (draft: CorrectionDraft) => void;
  readonly onApplyCorrection: () => void;
  readonly authoringDraftId: string | null;
  readonly authoringDraftError: string | null;
  readonly onOpenAuthoringDraft: () => void;
  readonly onRetryCreateAuthoringDraft: () => void;
}) {
  const report = props.run.validation_report;
  const draftable = canOpenImportRunInBuilder(props.run);
  const mode = props.mode ?? 'advanced';
  const isNormal = mode === 'normal';
  const [showAllIssues, setShowAllIssues] = useState(false);
  const staging = props.run.staging;
  const stagingSections =
    staging !== null && typeof staging === 'object' && Array.isArray(staging.sections)
      ? staging.sections
      : [];
  const stagingItems =
    staging !== null && typeof staging === 'object' && Array.isArray(staging.items)
      ? staging.items
      : [];
  const allIssues = report?.issues ?? [];
  const visibleIssues = showAllIssues ? allIssues : allIssues.slice(0, 10);
  const hasDraft = props.authoringDraftId !== null;
  const openInBuilderLabel = hasDraft
    ? isNormal && props.run.status === 'PUBLISH_BLOCKED'
      ? 'Перейти к черновику и исправить'
      : 'Перейти к черновику'
    : isNormal && props.run.status === 'PUBLISH_BLOCKED' && draftable
      ? 'Создать черновик и исправить'
      : isNormal
        ? 'Создать черновик'
        : 'Создать черновик конструктора';
  return (
    <>
      <p>
        Статус импорта: {importRunStatusLabel(props.run.status)}
        {report !== null
          ? ` · ошибок ${report.error_count} · предупреждений ${report.warning_count}`
          : ''}
      </p>
      {!isNormal && props.run.published_template_version_id !== null ? (
        <p role="status" className="banner">
          Версия {props.run.published_version_code} ранее опубликована устаревшим путём. Новый
          поток: ImportRun → черновик конструктора → publishTemplateAuthoringDraft.
        </p>
      ) : null}
      <h3>{isNormal ? 'Итог проверки' : 'Отчёт валидации'}</h3>
      {report === null ? (
        <p role="status">Отчёт ещё не готов.</p>
      ) : (
        <>
          <ul className="import-run-summary">
            <li>Разделов найдено: {stagingSections.length}</li>
            <li>Пунктов найдено: {stagingItems.length}</li>
            <li>Ошибок: {report.error_count}</li>
            <li>Предупреждений: {report.warning_count}</li>
          </ul>
              {allIssues.length === 0 ? (
            <p role="status" className="banner">
              {isNormal
                ? hasDraft
                  ? 'Ошибок нет. Черновик конструктора создан — можно перейти к нему.'
                  : 'Ошибок нет. Черновик конструктора будет доступен, если импорт готов.'
                : hasDraft
                  ? 'Ошибок нет. Черновик конструктора создан — можно перейти к нему.'
                  : 'Ошибок нет. Создание черновика конструктора доступно, если сервер пометил прогон как готовый.'}
            </p>
          ) : (
            <>
              {visibleIssues.map((issue, index) => {
                const context = issueContext(issue);
                return (
                  <div
                    key={`${issue.code}-${index}`}
                    role={issue.severity === 'ERROR' ? 'alert' : 'status'}
                    className={issue.severity === 'ERROR' ? 'banner banner-closed' : 'banner'}
                  >
                    <p>
                      {issue.severity === 'ERROR' ? 'Ошибка' : 'Предупреждение'}:{' '}
                      {validationIssueExplanation(issue.code)}
                    </p>
                    {!isNormal ? (
                      <p className="meta">
                        {issue.code}
                        {context !== null ? ` · ${context}` : ''}
                      </p>
                    ) : context !== null ? (
                      <p className="meta">{context}</p>
                    ) : null}
                  </div>
                );
              })}
              {allIssues.length > 10 && !showAllIssues ? (
                <button type="button" onClick={() => setShowAllIssues(true)}>
                  Показать все
                </button>
              ) : null}
            </>
          )}
        </>
      )}
      {!isNormal ? (
        <>
          <h3>Коррекция</h3>
          <p className="meta">
            Коррекция не изменяет исходный файл и его контрольную сумму. Неподдерживаемая
            классификация не принимается.
          </p>
          <CorrectionEditor
            draft={props.correction}
            disabled={props.busy}
            onChange={props.onCorrectionChange}
          />
          <button type="button" disabled={props.busy} onClick={props.onApplyCorrection}>
            Применить коррекцию
          </button>
          <details className="technical">
            <summary>Сформированная коррекция</summary>
            <pre>
              {props.correctionPreview.ok
                ? JSON.stringify(props.correctionPreview.correction, null, 2)
                : props.correctionPreview.failures.map((item) => item.message).join('\n')}
            </pre>
          </details>
        </>
      ) : null}
      {props.authoringDraftError !== null && !hasDraft ? (
        <p role="alert" className="banner banner-closed">
          Черновик не создан: {props.authoringDraftError}. Можно повторить создание из этого же
          результата импорта без повторной загрузки Excel.
        </p>
      ) : null}
      <button
        type="button"
        disabled={props.busy || (!hasDraft && !draftable)}
        onClick={() => {
          if (hasDraft) {
            props.onOpenAuthoringDraft();
            return;
          }
          props.onRetryCreateAuthoringDraft();
        }}
      >
        {openInBuilderLabel}
      </button>
      {!draftable && !hasDraft ? (
        <p className="meta">
          {isNormal
            ? 'Создание черновика недоступно, пока импорт не готов.'
            : 'Черновик недоступен, пока сервер не переведёт прогон в состояние «Готов к публикации».'}
        </p>
      ) : !isNormal ? (
        <p className="meta">
          Устаревший publishTemplateImportRun не используется. «Опубликовать» в конструкторе
          сохраняет неизменяемую версию; «Сделать активной» — отдельное подтверждаемое действие.
        </p>
      ) : hasDraft ? (
        <p className="meta">
          Повторный переход не создаёт второй черновик. Публикация и активация выполняются отдельно
          в конструкторе.
        </p>
      ) : null}
      {!isNormal ? (
        <TechnicalDetails>
          <p>Идентификатор прогона: {props.run.id}</p>
          <p>Оптимистичная версия: {props.run.version}</p>
          <p>Семейство источника: {props.sourceFamily || '—'}</p>
        </TechnicalDetails>
      ) : null}
    </>
  );
}

function MappingEditor(props: {
  readonly mapping: DeclarativeMapping;
  readonly disabled: boolean;
  readonly onChange: (mapping: DeclarativeMapping) => void;
}) {
  return (
    <>
      <label htmlFor="map_header_row">
        Строка заголовка
        <input
          id="map_header_row"
          type="number"
          min={1}
          value={props.mapping.header_row}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({ ...props.mapping, header_row: Number(event.target.value) })
          }
        />
      </label>
      <label htmlFor="map_data_start">
        Первая строка данных
        <input
          id="map_data_start"
          type="number"
          min={1}
          value={props.mapping.data_start_row}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({ ...props.mapping, data_start_row: Number(event.target.value) })
          }
        />
      </label>
      <label htmlFor="map_formula">
        Политика формул
        <select
          id="map_formula"
          value={props.mapping.formula_cells}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({
              ...props.mapping,
              formula_cells: event.target.value as FormulaCellPolicy,
            })
          }
        >
          {FORMULA_CELL_POLICIES.map((policy) => (
            <option key={policy} value={policy}>
              {formulaPolicyLabel(policy)}
            </option>
          ))}
        </select>
      </label>
      <h4>Колонки</h4>
      {COLUMN_NAMES.map((name) => (
        <ColumnBindingEditor
          key={name}
          name={name}
          binding={props.mapping.columns[name]}
          disabled={props.disabled}
          allowSourceOrder={name === 'item_ordinal'}
          onChange={(binding) => props.onChange(replaceColumn(props.mapping, name, binding))}
        />
      ))}
      <h4>Токены оценки</h4>
      {ASSESSMENTS.map((assessment) => (
        <label key={assessment} htmlFor={`map_token_${assessment}`}>
          {assessmentLabel(assessment)}
          <input
            id={`map_token_${assessment}`}
            value={formatTokenList(props.mapping.assessment_tokens[assessment])}
            disabled={props.disabled}
            onChange={(event) =>
              props.onChange(
                replaceTokens(props.mapping, assessment, parseTokenList(event.target.value)),
              )
            }
          />
        </label>
      ))}
      <h4>Доказательства FAIL</h4>
      {Object.entries(props.mapping.fail_evidence).map(([token, rule]) => (
        <FailEvidenceEditor
          key={token}
          token={token}
          rule={rule}
          disabled={props.disabled}
          onChange={(nextToken, nextRule) => {
            const next = { ...props.mapping.fail_evidence };
            delete next[token];
            if (nextToken.trim().length > 0) {
              next[nextToken.trim()] = nextRule;
            }
            props.onChange({ ...props.mapping, fail_evidence: next });
          }}
          onRemove={() => {
            const next = { ...props.mapping.fail_evidence };
            delete next[token];
            props.onChange({ ...props.mapping, fail_evidence: next });
          }}
        />
      ))}
      <button
        type="button"
        disabled={props.disabled}
        onClick={() =>
          props.onChange({
            ...props.mapping,
            fail_evidence: {
              ...props.mapping.fail_evidence,
              '': { assessment: 'FAIL', require: ['nonconformity_description', 'comment'] },
            },
          })
        }
      >
        Добавить доказательство FAIL
      </button>
      <h4>Поля чек-листа</h4>
      {props.mapping.fields.map((field, index) => (
        <FieldEditor
          key={`${field.field_key}-${index}`}
          index={index}
          field={field}
          disabled={props.disabled}
          onChange={(next) => {
            const fields = props.mapping.fields.map((item, itemIndex) =>
              itemIndex === index ? next : item,
            );
            props.onChange({ ...props.mapping, fields });
          }}
          onRemove={() =>
            props.onChange({
              ...props.mapping,
              fields: props.mapping.fields.filter((_, itemIndex) => itemIndex !== index),
            })
          }
        />
      ))}
      <button
        type="button"
        disabled={props.disabled}
        onClick={() =>
          props.onChange({ ...props.mapping, fields: [...props.mapping.fields, emptyField()] })
        }
      >
        Добавить поле
      </button>
    </>
  );
}

const HEADER_ALIAS_LABELS: Record<keyof HeaderAliasesV2, string> = {
  item_code: 'Код пункта',
  item_label: 'Название пункта',
  normative_reference: 'Нормативная ссылка',
  pass: 'Соответствует',
  fail: 'Не соответствует',
  not_applicable: 'Не применяется',
  comment: 'Примечание',
};

const ITEM_CODE_MODE_LABELS: Record<ItemCodeModeV2, string> = {
  HIERARCHICAL_NUMERIC: 'Иерархический числовой',
  INTEGER: 'Целое число',
  GENERATED: 'Сгенерированный',
};

function MappingEditorV2(props: {
  readonly mapping: DeclarativeMappingV2;
  readonly disabled: boolean;
  readonly onChange: (mapping: DeclarativeMappingV2) => void;
}) {
  const updateDiscovery = (patch: Partial<DeclarativeMappingV2['discovery']>) => {
    props.onChange({
      ...props.mapping,
      discovery: { ...props.mapping.discovery, ...patch },
    });
  };

  const updateAlias = (key: keyof HeaderAliasesV2, text: string) => {
    updateDiscovery({
      header_aliases: {
        ...props.mapping.discovery.header_aliases,
        [key]: parseTokenList(text),
      },
    });
  };

  const toggleMode = (mode: ItemCodeModeV2, enabled: boolean) => {
    const current = props.mapping.discovery.item_code_modes;
    const next = enabled
      ? ITEM_CODE_MODES_V2.filter((item) => current.includes(item) || item === mode)
      : current.filter((item) => item !== mode);
    updateDiscovery({ item_code_modes: next });
  };

  return (
    <>
      <label htmlFor="map_v2_formula">
        Политика формул
        <select
          id="map_v2_formula"
          value={props.mapping.formula_cells}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({
              ...props.mapping,
              formula_cells: event.target.value as FormulaCellPolicy,
            })
          }
        >
          {FORMULA_CELL_POLICIES.map((policy) => (
            <option key={policy} value={policy}>
              {formulaPolicyLabel(policy)}
            </option>
          ))}
        </select>
      </label>
      <h4>Регионы</h4>
      <label htmlFor="map_v2_scan_from_row">
        Строка начала сканирования (scan_from_row)
        <input
          id="map_v2_scan_from_row"
          type="number"
          min={1}
          value={props.mapping.discovery.scan_from_row}
          disabled={props.disabled}
          onChange={(event) => updateDiscovery({ scan_from_row: Number(event.target.value) })}
        />
      </label>
      <label htmlFor="map_v2_scan_to_row">
        Строка конца сканирования (scan_to_row)
        <input
          id="map_v2_scan_to_row"
          type="number"
          min={1}
          value={props.mapping.discovery.scan_to_row}
          disabled={props.disabled}
          onChange={(event) => updateDiscovery({ scan_to_row: Number(event.target.value) })}
        />
      </label>
      <label htmlFor="map_v2_allow_multiple_regions">
        <input
          id="map_v2_allow_multiple_regions"
          type="checkbox"
          checked={props.mapping.discovery.allow_multiple_regions}
          disabled={props.disabled}
          onChange={(event) => updateDiscovery({ allow_multiple_regions: event.target.checked })}
        />{' '}
        Разрешить несколько регионов
      </label>
      <label htmlFor="map_v2_max_non_item">
        Максимум пустых строк подряд
        <input
          id="map_v2_max_non_item"
          type="number"
          min={1}
          max={10}
          value={props.mapping.discovery.max_consecutive_non_item_rows}
          disabled={props.disabled}
          onChange={(event) =>
            updateDiscovery({ max_consecutive_non_item_rows: Number(event.target.value) })
          }
        />
      </label>
      <label htmlFor="map_v2_preceding_section">
        Строк заголовка раздела перед таблицей
        <input
          id="map_v2_preceding_section"
          type="number"
          min={0}
          max={3}
          value={props.mapping.discovery.preceding_section_title_rows}
          disabled={props.disabled}
          onChange={(event) =>
            updateDiscovery({ preceding_section_title_rows: Number(event.target.value) })
          }
        />
      </label>
      <h4>Псевдонимы заголовков</h4>
      {(Object.keys(HEADER_ALIAS_LABELS) as (keyof HeaderAliasesV2)[]).map((key) => (
        <label key={key} htmlFor={`map_v2_alias_${key}`}>
          {HEADER_ALIAS_LABELS[key]}
          <textarea
            id={`map_v2_alias_${key}`}
            rows={2}
            value={formatTokenList(props.mapping.discovery.header_aliases[key])}
            disabled={props.disabled}
            onChange={(event) => updateAlias(key, event.target.value)}
          />
        </label>
      ))}
      <h4>Режимы кода пункта</h4>
      {ITEM_CODE_MODES_V2.map((mode) => (
        <label key={mode} htmlFor={`map_v2_mode_${mode}`}>
          <input
            id={`map_v2_mode_${mode}`}
            type="checkbox"
            checked={props.mapping.discovery.item_code_modes.includes(mode)}
            disabled={props.disabled}
            onChange={(event) => toggleMode(mode, event.target.checked)}
          />{' '}
          {ITEM_CODE_MODE_LABELS[mode]}
        </label>
      ))}
      <h4>Токены оценки</h4>
      {ASSESSMENTS.map((assessment) => (
        <label key={assessment} htmlFor={`map_v2_token_${assessment}`}>
          {assessmentLabel(assessment)}
          <textarea
            id={`map_v2_token_${assessment}`}
            rows={2}
            value={formatTokenList(props.mapping.assessment_tokens[assessment])}
            disabled={props.disabled}
            onChange={(event) =>
              props.onChange({
                ...props.mapping,
                assessment_tokens: {
                  ...props.mapping.assessment_tokens,
                  [assessment]: parseTokenList(event.target.value),
                },
              })
            }
          />
        </label>
      ))}
      <h4>Доказательства FAIL</h4>
      {Object.entries(props.mapping.fail_evidence).map(([token, rule]) => (
        <FailEvidenceEditor
          key={token}
          token={token}
          rule={rule}
          disabled={props.disabled}
          onChange={(nextToken, nextRule) => {
            const next = { ...props.mapping.fail_evidence };
            delete next[token];
            if (nextToken.trim().length > 0) {
              next[nextToken.trim()] = nextRule;
            }
            props.onChange({ ...props.mapping, fail_evidence: next });
          }}
          onRemove={() => {
            const next = { ...props.mapping.fail_evidence };
            delete next[token];
            props.onChange({ ...props.mapping, fail_evidence: next });
          }}
        />
      ))}
      <button
        type="button"
        disabled={props.disabled}
        onClick={() =>
          props.onChange({
            ...props.mapping,
            fail_evidence: {
              ...props.mapping.fail_evidence,
              '': { assessment: 'FAIL', require: ['nonconformity_description', 'comment'] },
            },
          })
        }
      >
        Добавить доказательство FAIL
      </button>
      <h4>Поля чек-листа</h4>
      {props.mapping.fields.map((field, index) => (
        <FieldEditor
          key={`${field.field_key}-${index}`}
          index={index}
          field={field}
          disabled={props.disabled}
          onChange={(next) => {
            const fields = props.mapping.fields.map((item, itemIndex) =>
              itemIndex === index ? next : item,
            );
            props.onChange({ ...props.mapping, fields });
          }}
          onRemove={() =>
            props.onChange({
              ...props.mapping,
              fields: props.mapping.fields.filter((_, itemIndex) => itemIndex !== index),
            })
          }
        />
      ))}
      <button
        type="button"
        disabled={props.disabled}
        onClick={() =>
          props.onChange({ ...props.mapping, fields: [...props.mapping.fields, emptyField()] })
        }
      >
        Добавить поле
      </button>
    </>
  );
}

function CorrectionEditor(props: {
  readonly draft: CorrectionDraft;
  readonly disabled: boolean;
  readonly onChange: (draft: CorrectionDraft) => void;
}) {
  return (
    <>
      <label htmlFor="corr_include_tokens">
        <input
          id="corr_include_tokens"
          type="checkbox"
          checked={props.draft.includeAssessmentTokens}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({ ...props.draft, includeAssessmentTokens: event.target.checked })
          }
        />{' '}
        Изменить токены оценки
      </label>
      {props.draft.includeAssessmentTokens
        ? ASSESSMENTS.map((assessment) => (
            <label key={assessment} htmlFor={`corr_token_${assessment}`}>
              {assessmentLabel(assessment)}
              <input
                id={`corr_token_${assessment}`}
                value={props.draft.assessmentTokens[assessment]}
                disabled={props.disabled}
                onChange={(event) =>
                  props.onChange({
                    ...props.draft,
                    assessmentTokens: {
                      ...props.draft.assessmentTokens,
                      [assessment]: event.target.value,
                    },
                  })
                }
              />
            </label>
          ))
        : null}
      <h4>Доказательства FAIL</h4>
      {props.draft.failEvidenceRows.map((row, index) => (
        <div key={`corr-ev-${index}`}>
          <label htmlFor={`corr_ev_token_${index}`}>
            Исходный токен
            <input
              id={`corr_ev_token_${index}`}
              value={row.token}
              disabled={props.disabled}
              onChange={(event) => {
                const failEvidenceRows = props.draft.failEvidenceRows.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, token: event.target.value } : item,
                );
                props.onChange({ ...props.draft, failEvidenceRows });
              }}
            />
          </label>
          <label htmlFor={`corr_ev_comment_${index}`}>
            <input
              id={`corr_ev_comment_${index}`}
              type="checkbox"
              checked={row.requireComment}
              disabled={props.disabled}
              onChange={(event) => {
                const failEvidenceRows = props.draft.failEvidenceRows.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, requireComment: event.target.checked } : item,
                );
                props.onChange({ ...props.draft, failEvidenceRows });
              }}
            />{' '}
            Требовать comment
          </label>
          <label htmlFor={`corr_ev_nc_${index}`}>
            <input
              id={`corr_ev_nc_${index}`}
              type="checkbox"
              checked={row.requireNonconformity}
              disabled={props.disabled}
              onChange={(event) => {
                const failEvidenceRows = props.draft.failEvidenceRows.map((item, itemIndex) =>
                  itemIndex === index
                    ? { ...item, requireNonconformity: event.target.checked }
                    : item,
                );
                props.onChange({ ...props.draft, failEvidenceRows });
              }}
            />{' '}
            Требовать nonconformity_description
          </label>
          <button
            type="button"
            disabled={props.disabled}
            onClick={() =>
              props.onChange({
                ...props.draft,
                failEvidenceRows: props.draft.failEvidenceRows.filter(
                  (_, itemIndex) => itemIndex !== index,
                ),
              })
            }
          >
            Удалить доказательство
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={props.disabled}
        onClick={() =>
          props.onChange({
            ...props.draft,
            failEvidenceRows: [
              ...props.draft.failEvidenceRows,
              { token: '', requireComment: true, requireNonconformity: true },
            ],
          })
        }
      >
        Добавить доказательство коррекции
      </button>
      <h4>Колонки</h4>
      {props.draft.columnOverrides.map((row) => (
        <div key={row.name}>
          <label htmlFor={`corr_col_on_${row.name}`}>
            <input
              id={`corr_col_on_${row.name}`}
              type="checkbox"
              checked={row.enabled}
              disabled={props.disabled}
              onChange={(event) =>
                props.onChange({
                  ...props.draft,
                  columnOverrides: props.draft.columnOverrides.map((item) =>
                    item.name === row.name ? { ...item, enabled: event.target.checked } : item,
                  ),
                })
              }
            />{' '}
            Изменить {columnNameLabel(row.name)}
          </label>
          {row.enabled ? (
            <ColumnBindingEditor
              name={row.name}
              binding={row.binding}
              disabled={props.disabled}
              allowSourceOrder={row.name === 'item_ordinal'}
              onChange={(binding) =>
                props.onChange({
                  ...props.draft,
                  columnOverrides: props.draft.columnOverrides.map((item) =>
                    item.name === row.name ? { ...item, binding } : item,
                  ),
                })
              }
            />
          ) : null}
        </div>
      ))}
      <label htmlFor="corr_include_fields">
        <input
          id="corr_include_fields"
          type="checkbox"
          checked={props.draft.includeFields}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({ ...props.draft, includeFields: event.target.checked })
          }
        />{' '}
        Заменить набор полей
      </label>
      {props.draft.includeFields ? (
        <>
          {props.draft.fields.map((field, index) => (
            <FieldEditor
              key={`corr-field-${index}`}
              index={index}
              field={field}
              disabled={props.disabled}
              onChange={(next) =>
                props.onChange({
                  ...props.draft,
                  fields: props.draft.fields.map((item, itemIndex) =>
                    itemIndex === index ? next : item,
                  ),
                })
              }
              onRemove={() =>
                props.onChange({
                  ...props.draft,
                  fields: props.draft.fields.filter((_, itemIndex) => itemIndex !== index),
                })
              }
            />
          ))}
          <button
            type="button"
            disabled={props.disabled}
            onClick={() =>
              props.onChange({ ...props.draft, fields: [...props.draft.fields, emptyField()] })
            }
          >
            Добавить поле коррекции
          </button>
        </>
      ) : null}
      <h4>Переименование кодов пунктов</h4>
      {props.draft.itemCodeRewrites.map((row, index) => (
        <div key={`rewrite-${index}`}>
          <label htmlFor={`corr_rewrite_from_${index}`}>
            Исходный код
            <input
              id={`corr_rewrite_from_${index}`}
              value={row.from}
              disabled={props.disabled}
              onChange={(event) =>
                props.onChange({
                  ...props.draft,
                  itemCodeRewrites: props.draft.itemCodeRewrites.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, from: event.target.value } : item,
                  ),
                })
              }
            />
          </label>
          <label htmlFor={`corr_rewrite_to_${index}`}>
            Новый код
            <input
              id={`corr_rewrite_to_${index}`}
              value={row.to}
              disabled={props.disabled}
              onChange={(event) =>
                props.onChange({
                  ...props.draft,
                  itemCodeRewrites: props.draft.itemCodeRewrites.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, to: event.target.value } : item,
                  ),
                })
              }
            />
          </label>
          <button
            type="button"
            disabled={props.disabled}
            onClick={() =>
              props.onChange({
                ...props.draft,
                itemCodeRewrites: props.draft.itemCodeRewrites.filter(
                  (_, itemIndex) => itemIndex !== index,
                ),
              })
            }
          >
            Удалить переименование
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={props.disabled}
        onClick={() =>
          props.onChange({
            ...props.draft,
            itemCodeRewrites: [...props.draft.itemCodeRewrites, { from: '', to: '' }],
          })
        }
      >
        Добавить переименование кода
      </button>
      <label htmlFor="corr_formula">
        Политика формул коррекции
        <select
          id="corr_formula"
          value={props.draft.formulaCells}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({
              ...props.draft,
              formulaCells: event.target.value as CorrectionDraft['formulaCells'],
            })
          }
        >
          <option value="">Не менять</option>
          {FORMULA_CELL_POLICIES.map((policy) => (
            <option key={policy} value={policy}>
              {formulaPolicyLabel(policy)}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}

function ColumnBindingEditor(props: {
  readonly name: ColumnName;
  readonly binding: ColumnBinding;
  readonly disabled: boolean;
  readonly allowSourceOrder: boolean;
  readonly onChange: (binding: ColumnBinding) => void;
}) {
  return (
    <fieldset>
      <legend>{columnNameLabel(props.name)}</legend>
      <label htmlFor={`bind_kind_${props.name}`}>
        Тип привязки
        <select
          id={`bind_kind_${props.name}`}
          value={props.binding.kind}
          disabled={props.disabled}
          onChange={(event) => {
            const kind = event.target.value;
            if (kind === 'column') {
              props.onChange({ kind: 'column', column: 'A' });
              return;
            }
            if (kind === 'fixed') {
              props.onChange({ kind: 'fixed', value: '' });
              return;
            }
            if (kind === 'source_order') {
              props.onChange({ kind: 'source_order' });
              return;
            }
            props.onChange({ kind: 'absent' });
          }}
        >
          <option value="column">{bindingKindLabel('column')}</option>
          <option value="fixed">{bindingKindLabel('fixed')}</option>
          <option value="absent">{bindingKindLabel('absent')}</option>
          {props.allowSourceOrder ? (
            <option value="source_order">{bindingKindLabel('source_order')}</option>
          ) : null}
        </select>
      </label>
      {props.binding.kind === 'column' ? (
        <label htmlFor={`bind_col_${props.name}`}>
          Колонка
          <input
            id={`bind_col_${props.name}`}
            value={props.binding.column}
            disabled={props.disabled}
            onChange={(event) =>
              props.onChange({ kind: 'column', column: event.target.value.toUpperCase() })
            }
          />
        </label>
      ) : null}
      {props.binding.kind === 'fixed' ? (
        <label htmlFor={`bind_fixed_${props.name}`}>
          Значение
          <input
            id={`bind_fixed_${props.name}`}
            value={props.binding.value}
            disabled={props.disabled}
            onChange={(event) => props.onChange({ kind: 'fixed', value: event.target.value })}
          />
        </label>
      ) : null}
    </fieldset>
  );
}

function FailEvidenceEditor(props: {
  readonly token: string;
  readonly rule: FailEvidenceRule;
  readonly disabled: boolean;
  readonly onChange: (token: string, rule: FailEvidenceRule) => void;
  readonly onRemove: () => void;
}) {
  const requireComment = props.rule.require.includes('comment');
  const requireNonconformity = props.rule.require.includes('nonconformity_description');
  return (
    <div>
      <label htmlFor={`fe_token_${props.token || 'new'}`}>
        Исходный токен
        <input
          id={`fe_token_${props.token || 'new'}`}
          value={props.token}
          disabled={props.disabled}
          onChange={(event) => props.onChange(event.target.value, props.rule)}
        />
      </label>
      <label htmlFor={`fe_comment_${props.token || 'new'}`}>
        <input
          id={`fe_comment_${props.token || 'new'}`}
          type="checkbox"
          checked={requireComment}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange(props.token, {
              assessment: 'FAIL',
              require: [
                ...(requireNonconformity ? (['nonconformity_description'] as const) : []),
                ...(event.target.checked ? (['comment'] as const) : []),
              ],
            })
          }
        />{' '}
        Требовать comment
      </label>
      <label htmlFor={`fe_nc_${props.token || 'new'}`}>
        <input
          id={`fe_nc_${props.token || 'new'}`}
          type="checkbox"
          checked={requireNonconformity}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange(props.token, {
              assessment: 'FAIL',
              require: [
                ...(event.target.checked ? (['nonconformity_description'] as const) : []),
                ...(requireComment ? (['comment'] as const) : []),
              ],
            })
          }
        />{' '}
        Требовать nonconformity_description
      </label>
      <button type="button" disabled={props.disabled} onClick={props.onRemove}>
        Удалить доказательство
      </button>
    </div>
  );
}

function FieldEditor(props: {
  readonly index: number;
  readonly field: FieldMapping;
  readonly disabled: boolean;
  readonly onChange: (field: FieldMapping) => void;
  readonly onRemove: () => void;
}) {
  const id = `field_${props.index}`;
  return (
    <fieldset>
      <legend>Поле {props.index + 1}</legend>
      <label htmlFor={`${id}_key`}>
        Ключ поля
        <input
          id={`${id}_key`}
          value={props.field.field_key}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...props.field, field_key: event.target.value })}
        />
      </label>
      <label htmlFor={`${id}_label`}>
        Подпись
        <input
          id={`${id}_label`}
          value={props.field.label}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...props.field, label: event.target.value })}
        />
      </label>
      <label htmlFor={`${id}_binding`}>
        Вид привязки
        <select
          id={`${id}_binding`}
          value={props.field.binding_kind}
          disabled={props.disabled}
          onChange={(event) => {
            const binding_kind = event.target.value as FieldMapping['binding_kind'];
            props.onChange({
              ...props.field,
              binding_kind,
              snapshot_path:
                binding_kind === 'CASE_SNAPSHOT' ? (props.field.snapshot_path ?? '') : null,
            });
          }}
        >
          {BINDING_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {kind}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={`${id}_type`}>
        Тип значения
        <select
          id={`${id}_type`}
          value={props.field.value_type}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({
              ...props.field,
              value_type: event.target.value as FieldMapping['value_type'],
            })
          }
        >
          {VALUE_TYPES.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={`${id}_owner`}>
        Владелец
        <select
          id={`${id}_owner`}
          value={props.field.owner_code}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({
              ...props.field,
              owner_code: event.target.value as FieldMapping['owner_code'],
            })
          }
        >
          {OWNER_CODES.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={`${id}_placement`}>
        Размещение
        <select
          id={`${id}_placement`}
          value={props.field.placement}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({
              ...props.field,
              placement: event.target.value as FieldMapping['placement'],
            })
          }
        >
          {PLACEMENTS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </label>
      {props.field.binding_kind === 'CASE_SNAPSHOT' ? (
        <label htmlFor={`${id}_snapshot`}>
          Путь снимка
          <input
            id={`${id}_snapshot`}
            value={props.field.snapshot_path ?? ''}
            disabled={props.disabled}
            onChange={(event) =>
              props.onChange({ ...props.field, snapshot_path: event.target.value })
            }
          />
        </label>
      ) : null}
      <p className="meta">
        Декларативные правила поля сохранены как есть и не редактируются текстом JSON.
      </p>
      <button type="button" disabled={props.disabled} onClick={props.onRemove}>
        Удалить поле
      </button>
    </fieldset>
  );
}

function QualificationSection(props: {
  readonly api: AdminApi;
  readonly templates: readonly ChecklistTemplateAdmin[];
  readonly users: readonly UserSummary[];
}) {
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<InspectorQualification[] | null>(null);
  const [userId, setUserId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const inspectors = useMemo(() => inspectorCandidates(props.users), [props.users]);
  const listLoaded = items !== null;
  const current = existingQualification(items ?? [], userId, templateId);

  const loadQualifications = async () => {
    const next = await props.api.listQualifications();
    setItems(next);
    return next;
  };

  useEffect(() => {
    void loadQualifications().catch((failure: unknown) => setError(failure));
  }, [props.api]);

  const userById = new Map(props.users.map((row) => [row.id, row]));
  const templateById = new Map(props.templates.map((row) => [row.id, row]));

  return (
    <>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {success !== null ? (
        <p role="status" className="banner">
          {success}
        </p>
      ) : null}
      <p className="meta" role="note">
        Допуск определяет, с какими чек-листами инспектор имеет право работать. Он не назначает
        инспектора на конкретный осмотр.
      </p>
      <label htmlFor="qual_user">
        Инспектор
        <select
          id="qual_user"
          value={userId}
          disabled={busy}
          onChange={(event) => setUserId(event.target.value)}
        >
          <option value="">Выберите пользователя</option>
          {inspectors.map((row) => (
            <option key={row.id} value={row.id}>
              {userBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor="qual_template">
        Чек-лист
        <select
          id="qual_template"
          value={templateId}
          disabled={busy}
          onChange={(event) => setTemplateId(event.target.value)}
        >
          <option value="">Выберите чек-лист</option>
          {props.templates.map((row) => (
            <option key={row.id} value={row.id}>
              {templateBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor="qual_status">
        Статус допуска
        <select
          id="qual_status"
          value={status}
          disabled={busy}
          onChange={(event) => setStatus(event.target.value as 'ACTIVE' | 'INACTIVE')}
        >
          <option value="ACTIVE">{qualificationStatusLabel('ACTIVE')}</option>
          <option value="INACTIVE">{qualificationStatusLabel('INACTIVE')}</option>
        </select>
      </label>
      <p className="meta">
        {items === null
          ? 'Список допусков загружается.'
          : current === null
            ? 'Новый допуск.'
            : `Текущий допуск: ${qualificationStatusLabel(current.status)} · версия ${current.version}`}
      </p>
      <button
        type="button"
        disabled={busy || !listLoaded || userId.length === 0 || templateId.length === 0}
        onClick={() => {
          if (busy || items === null || userId.length === 0 || templateId.length === 0) {
            return;
          }
          const expectedVersion = current?.version ?? null;
          setBusy(true);
          setError(null);
          setSuccess(null);
          void props.api
            .putQualification({
              user_id: userId,
              checklist_template_id: templateId,
              status,
              expected_version: expectedVersion,
            })
            .then(async () => {
              setSuccess('Допуск сохранён.');
              try {
                await loadQualifications();
              } catch (failure: unknown) {
                setItems(null);
                setError(failure);
              }
            })
            .catch((failure: unknown) => setError(failure))
            .finally(() => setBusy(false));
        }}
      >
        Сохранить допуск
      </button>
      {items === null ? <p role="status">Загрузка…</p> : null}
      {items !== null && items.length === 0 ? (
        <p role="status" className="banner">
          Допуски ещё не назначены.
        </p>
      ) : null}
      {items !== null && items.length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Инспектор</th>
              <th>Чек-лист</th>
              <th>Допуск</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const user = userById.get(row.user_id);
              const template = templateById.get(row.checklist_template_id);
              return (
                <tr key={row.id}>
                  <td>{user === undefined ? 'Пользователь' : userBusinessLabel(user)}</td>
                  <td>{template === undefined ? 'чек-лист' : templateBusinessLabel(template)}</td>
                  <td>{qualificationStatusLabel(row.status)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

function AssignmentSection(props: {
  readonly api: AdminApi;
  readonly templates: readonly ChecklistTemplateAdmin[];
  readonly users: readonly UserSummary[];
}) {
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cases, setCases] = useState<InspectionCaseSummary[] | null>(null);
  const [caseId, setCaseId] = useState('');
  const [detail, setDetail] = useState<InspectionCaseDetail | null>(null);
  const [instanceId, setInstanceId] = useState('');
  const [userId, setUserId] = useState('');
  const [checklistLabels, setChecklistLabels] = useState<Record<string, string>>({});
  const [caseLoading, setCaseLoading] = useState(false);
  const [simplifiedAccess, setSimplifiedAccess] = useState(false);
  const [savedSimplifiedAccess, setSavedSimplifiedAccess] = useState(false);
  const [policyReady, setPolicyReady] = useState(false);
  const caseLoadSeq = useRef(0);
  const caseIdRef = useRef('');
  const users = useMemo(() => assignmentUserCandidates(props.users), [props.users]);
  const caseMatchesSelection =
    !caseLoading && detail !== null && detail.id === caseId && caseId.length > 0;
  const instanceBelongsToDetail =
    detail !== null && detail.checklist_instances.some((row) => row.id === instanceId);
  const current =
    detail === null || !instanceBelongsToDetail
      ? null
      : currentChecklistAssignment(detail.assignments, instanceId);

  useEffect(() => {
    void props.api
      .listInspectionCases()
      .then(setCases)
      .catch((failure: unknown) => setError(failure));
  }, [props.api]);

  useEffect(() => {
    let cancelled = false;
    void props.api
      .getInspectorAccessPolicy()
      .then((policy) => {
        if (cancelled) {
          return;
        }
        setSimplifiedAccess(policy.simplified_inspector_access === true);
        setSavedSimplifiedAccess(policy.simplified_inspector_access === true);
        setPolicyReady(true);
      })
      .catch((failure: unknown) => {
        if (!cancelled) {
          setError(failure);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.api]);

  const saveSimplifiedAccess = () => {
    if (!policyReady || busy) {
      return;
    }
    const next = simplifiedAccess;
    setBusy(true);
    setError(null);
    setSuccess(null);
    void props.api
      .putInspectorAccessPolicy(next)
      .then((policy) => {
        const saved = policy.simplified_inspector_access === true;
        setSimplifiedAccess(saved);
        setSavedSimplifiedAccess(saved);
        setSuccess(
          saved
            ? 'Упрощённый режим инспекторов включён.'
            : 'Упрощённый режим инспекторов выключен. Действуют прежние правила.',
        );
      })
      .catch((failure: unknown) => {
        setSimplifiedAccess(savedSimplifiedAccess);
        setError(failure);
      })
      .finally(() => setBusy(false));
  };

  const loadCase = (id: string) => {
    const seq = caseLoadSeq.current + 1;
    caseLoadSeq.current = seq;
    caseIdRef.current = id;
    setCaseId(id);
    setDetail(null);
    setInstanceId('');
    setChecklistLabels({});
    setError(null);
    setSuccess(null);
    if (id.length === 0) {
      setCaseLoading(false);
      return;
    }
    setCaseLoading(true);
    void props.api
      .getInspectionCase(id)
      .then(async (loaded) => {
        if (seq !== caseLoadSeq.current || caseIdRef.current !== id) {
          return;
        }
        const labels: Record<string, string> = {};
        for (const instance of loaded.checklist_instances) {
          if (seq !== caseLoadSeq.current || caseIdRef.current !== id) {
            return;
          }
          const version = await props.api.getTemplateVersion(instance.template_version_id);
          if (seq !== caseLoadSeq.current || caseIdRef.current !== id) {
            return;
          }
          labels[instance.id] = `${version.template_code} · ${version.version_code}`;
        }
        if (seq !== caseLoadSeq.current || caseIdRef.current !== id) {
          return;
        }
        setDetail(loaded);
        setChecklistLabels(labels);
        setInstanceId(loaded.checklist_instances[0]?.id ?? '');
        setCaseLoading(false);
      })
      .catch((failure: unknown) => {
        if (seq !== caseLoadSeq.current || caseIdRef.current !== id) {
          return;
        }
        setDetail(null);
        setChecklistLabels({});
        setInstanceId('');
        setCaseLoading(false);
        setError(failure);
      });
  };

  const selectedCase = cases?.find((row) => row.id === caseId) ?? null;
  const assignedUser =
    current === null ? null : props.users.find((row) => row.id === current.user_id);

  return (
    <>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {success !== null ? (
        <p role="status" className="banner">
          {success}
        </p>
      ) : null}
      <label htmlFor="asg_simplified_access">
        <input
          id="asg_simplified_access"
          type="checkbox"
          checked={simplifiedAccess}
          disabled={!policyReady || busy}
          onChange={(event) => setSimplifiedAccess(event.target.checked)}
        />{' '}
        Упрощённый режим инспекторов (без назначений и квалификаций)
      </label>
      <p className="meta" role="note">
        Все активные инспекторы видят доступные осмотры и могут проводить их, завершать, подписывать
        и отправлять результат без назначения на чек-лист и дополнительных допусков. При отключении
        действуют прежние правила.
      </p>
      {savedSimplifiedAccess ? (
        <p className="meta" role="note">
          Ручные назначения не ограничивают осмотры, открытые упрощённым режимом, и не удаляются.
        </p>
      ) : null}
      <div className="actions">
        <button
          type="button"
          disabled={!policyReady || busy || simplifiedAccess === savedSimplifiedAccess}
          onClick={saveSimplifiedAccess}
        >
          Сохранить режим
        </button>
      </div>
      <p className="meta" role="note">
        Назначение связывает конкретный чек-лист конкретного осмотра с инспектором. Перед
        сохранением сервер отдельно проверяет роль и допуск инспектора.
      </p>
      <label htmlFor="asg_case">
        Осмотр
        <select
          id="asg_case"
          value={caseId}
          disabled={busy}
          onChange={(event) => loadCase(event.target.value)}
        >
          <option value="">Выберите осмотр</option>
          {(cases ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {caseBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      {cases !== null && cases.length === 0 ? (
        <p role="status" className="banner">
          Осмотры не найдены.
        </p>
      ) : null}
      {caseLoading ? <p role="status">Загрузка…</p> : null}
      <label htmlFor="asg_instance">
        Чек-лист
        <select
          id="asg_instance"
          value={instanceId}
          disabled={busy || caseLoading || !caseMatchesSelection}
          onChange={(event) => setInstanceId(event.target.value)}
        >
          <option value="">Выберите чек-лист</option>
          {(detail?.checklist_instances ?? []).map((row) => (
            <option key={row.id} value={row.id} disabled={checklistLabels[row.id] === undefined}>
              {checklistLabels[row.id] ?? 'Идентичность чек-листа не загружена'}
            </option>
          ))}
        </select>
      </label>
      <p>
        Текущее назначение:{' '}
        {current === null
          ? 'нет'
          : assignedUser === undefined || assignedUser === null
            ? 'назначено'
            : userBusinessLabel(assignedUser)}
        {caseMatchesSelection && detail !== null ? ` · версия осмотра ${detail.version}` : ''}
      </p>
      <label htmlFor="asg_user">
        Инспектор
        <select
          id="asg_user"
          value={userId}
          disabled={busy}
          onChange={(event) => setUserId(event.target.value)}
        >
          <option value="">Выберите пользователя</option>
          {users.map((row) => (
            <option key={row.id} value={row.id}>
              {userBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <div className="actions">
        <button
          type="button"
          disabled={
            busy ||
            caseLoading ||
            !caseMatchesSelection ||
            !instanceBelongsToDetail ||
            instanceId.length === 0 ||
            userId.length === 0 ||
            checklistLabels[instanceId] === undefined
          }
          onClick={() => {
            if (
              detail === null ||
              busy ||
              caseLoading ||
              detail.id !== caseId ||
              !detail.checklist_instances.some((row) => row.id === instanceId) ||
              checklistLabels[instanceId] === undefined
            ) {
              return;
            }
            const targetCaseId = detail.id;
            const targetInstanceId = instanceId;
            const expectedVersion = detail.version;
            const seq = caseLoadSeq.current;
            setBusy(true);
            setError(null);
            setSuccess(null);
            void props.api
              .putChecklistInspectorAssignment(
                targetCaseId,
                targetInstanceId,
                expectedVersion,
                userId,
              )
              .then(() => props.api.getInspectionCase(targetCaseId))
              .then((fresh) => {
                if (seq !== caseLoadSeq.current || caseIdRef.current !== targetCaseId) {
                  return;
                }
                setDetail(fresh);
                setSuccess('Назначение сохранено.');
              })
              .catch((failure: unknown) => {
                if (seq !== caseLoadSeq.current || caseIdRef.current !== targetCaseId) {
                  return;
                }
                setError(failure);
              })
              .finally(() => setBusy(false));
          }}
        >
          Назначить инспектора
        </button>
        <button
          type="button"
          disabled={
            busy ||
            caseLoading ||
            !caseMatchesSelection ||
            !instanceBelongsToDetail ||
            instanceId.length === 0
          }
          onClick={() => {
            if (
              detail === null ||
              busy ||
              caseLoading ||
              detail.id !== caseId ||
              !detail.checklist_instances.some((row) => row.id === instanceId)
            ) {
              return;
            }
            const targetCaseId = detail.id;
            const targetInstanceId = instanceId;
            const expectedVersion = detail.version;
            const seq = caseLoadSeq.current;
            setBusy(true);
            setError(null);
            setSuccess(null);
            void props.api
              .putChecklistInspectorAssignment(
                targetCaseId,
                targetInstanceId,
                expectedVersion,
                null,
              )
              .then(() => props.api.getInspectionCase(targetCaseId))
              .then((fresh) => {
                if (seq !== caseLoadSeq.current || caseIdRef.current !== targetCaseId) {
                  return;
                }
                setDetail(fresh);
                setUserId('');
                setSuccess('Назначение снято.');
              })
              .catch((failure: unknown) => {
                if (seq !== caseLoadSeq.current || caseIdRef.current !== targetCaseId) {
                  return;
                }
                setError(failure);
              })
              .finally(() => setBusy(false));
          }}
        >
          Снять назначение
        </button>
      </div>
      <p className="meta">
        Допуск не создаётся автоматически. Сервер проверяет роль, допуск и назначение отдельно.
      </p>
      {selectedCase !== null ? (
        <TechnicalDetails>
          <p>Идентификатор осмотра: {selectedCase.id}</p>
          {instanceId.length > 0 ? <p>Идентификатор чек-листа: {instanceId}</p> : null}
        </TechnicalDetails>
      ) : null}
    </>
  );
}
